import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import type { UIMessage } from "ai"
import { IddRunPanel } from "@/components/idd/idd-run-panel"
import { useIddRunStand } from "@/lib/idd-queries"
import { useQueryClient } from "@tanstack/react-query"
import {
  processRunControllerGetQueryKey,
  reflectionControllerGetQueryKey,
  sessionsControllerGetQueryKey,
} from "@/client/@tanstack/react-query.gen"
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  Calculator,
  ClipboardList,
  Clock,
  Info,
  Loader2,
  Maximize2,
  Minimize2,
  Phone,
  RefreshCw,
  Sparkles,
  X,
} from "lucide-react"
import { toast } from "sonner"

import {
  processRunControllerGet,
  sessionsControllerHeartbeat,
} from "@/client/sdk.gen"
import type { ProcessRunDto, TrainingSessionDto } from "@/client/types.gen"
import { FeedbackPanel, type FeedbackLike } from "@/components/feedback-panel"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { CaseBriefingCard } from "@/components/case-briefing-card"
import { PersonaInfoDialog } from "@/components/persona-info-dialog"
import { ChatHandle } from "@/components/chat-handle"
import { CoachBubble } from "@/components/coach-bubble"
import { CoachChat } from "@/components/coach-chat"
import { hatFeedbackFestgehalten } from "@/components/coach-tool-call"
import { CoachThread } from "@/components/coach-thread"
import { DebriefReport, DebriefSkeleton } from "@/components/debrief-report"
import { PhaseScopeNote } from "@/components/phase-scope-note"
import { PrintButton } from "@/components/print-button"
import { VorleseButton } from "@/components/vorlese-button"
import { ReportFooter, ReportMasthead } from "@/components/report-masthead"
import { initials, RoleplayChat } from "@/components/roleplay-chat"
import { AvToolFenster } from "@/components/av-tool-fenster"
import { LoesungPanel } from "@/components/loesung-panel"
import {
  checklisteErledigt,
  freigabeHinweis,
  leitfadenFuer,
  PhasenAktionen,
  PhasenLeitfaden,
} from "@/components/phasen-leitfaden"
import { RunCoachChat } from "@/components/run-coach-chat"
import { SelectionQuoteArea } from "@/components/selection-quote-area"
import { VoiceSessionPanel } from "@/components/voice-session-overlay"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Textarea } from "@/components/ui/textarea"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { apiFehlerDetail } from "@/lib/api-client"
import { cn } from "@/lib/utils"
import { useChecklistenHaken } from "@/lib/preferences"
import { useDebriefStream } from "@/lib/use-debrief-stream"
import { useObjectStream } from "@/lib/use-object-stream"
import { useMediaQuery } from "@/lib/use-media-query"
import { SHOW_IDD_TIME } from "@/lib/feature-flags"
import {
  coachCallHint,
  coachPossessive,
  useCoachById,
  useCoachFor,
} from "@/lib/use-coach"
import { formatHMS, useActiveTrainingTime } from "@/lib/idd/active-time"
import { einheitenMarken, sessionForUnit } from "@/lib/run-phases"
import {
  useAdvanceReflection,
  useAdvanceStage,
  useSaveAvTool,
  useSaveLoesung,
  usePersonas,
  useProcessRun,
  useReflection,
  useSession,
  useSubmitNachbereitung,
  useSubmitPreparation,
} from "@/lib/queries"

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import {
  feedbackSchema,
  COACH_FALLBACK_LABEL,
  EINHEIT_LABELS,
  GESPRAECHSTRAINING_LABEL,
  NACHBEREITUNG_FRAGEN,
  einheitDefinition,
  einheitenVorgeschichte,
  fortsetzungVon,
  istGesamtbild,
  phasenDerEinheit,
  zeigtEigenesDokument,
  aktionsZeile,
  arbeitsblattFelder,
  arbeitsblattStand,
  avToolGezeigtText,
  berechneAvTool,
  type AvToolEingabe,
  type LoesungVorschlag,
  type TrainingsEinheit,
} from "@nl/shared"

type Persona = NonNullable<ReturnType<typeof usePersonas>["data"]>[number]

/**
 * What the advisor does in this unit, one line long.
 *
 * Only the appointment call needs the customer's name in the text — she is
 * calling him, after all. The other lines live on the unit definition itself.
 */
const EINHEIT_DESC: Partial<
  Record<TrainingsEinheit, (name: string) => string>
> = {
  terminierung: (name) =>
    `Rufe ${name} an: über den Anlass ins Gespräch kommen, Einwände auffangen, Termin vereinbaren.`,
}

function einheitBeschreibung(einheit: TrainingsEinheit, name: string): string {
  return EINHEIT_DESC[einheit]?.(name) ?? einheitDefinition(einheit).description
}

// --- Editorial progress rail, driven by the run's configured phase plan ---
/**
 * Die Leiste ist Navigation, nicht nur Anzeige.
 *
 * Every unit that already happened can be opened again, and the running one
 * leads back into the conversation. Without that a unit's evaluation would only
 * be reachable in the moment it is finished.
 *
 * What is marked is what is on screen (`viewing`), not where the run stands:
 * otherwise paging back lights up the wrong unit.
 */
function ProgressRail({
  run,
  viewing,
  imAbschluss = false,
  onSelect,
}: {
  run: ProcessRunDto
  /** The unit whose content is on screen. */
  viewing?: TrainingsEinheit
  /** The shown unit is being wrapped up; the next one has not started. */
  imAbschluss?: boolean
  onSelect?: (unit: TrainingsEinheit) => void
}) {
  const units = run.units as TrainingsEinheit[]
  const completed = run.status === "completed"
  const marken = einheitenMarken(
    units,
    run.currentUnit as TrainingsEinheit | undefined,
    completed,
    imAbschluss
  )
  const gezeigterSchritt = viewing ? units.indexOf(viewing) : -1
  const schritt =
    gezeigterSchritt >= 0
      ? gezeigterSchritt
      : Math.min(
          marken.filter((m) => m === "erledigt").length,
          units.length - 1
        )

  return (
    <div
      className="grid"
      style={{
        gridTemplateColumns: `repeat(${units.length}, minmax(0, 1fr))`,
      }}
      aria-label={`Schritt ${schritt + 1} von ${units.length}`}
    >
      {units.map((p, i) => {
        const done = marken[i] === "erledigt"
        const laufend = marken[i] === "laufend"
        const gezeigt = viewing === p
        // Die Schlussstation trägt keine Sitzung; ihr Inhalt ist der Bericht,
        // und der ist nach dem Abschluss weiter abrufbar.
        const hatInhalt = done && (istGesamtbild(p) || !!sessionForUnit(run, p))
        const anklickbar = !!onSelect && !gezeigt && (hatInhalt || laufend)

        const inhalt = (
          <>
            <span
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-sm font-mono text-[11px] tabular-nums",
                (done || laufend) && "bg-primary text-primary-foreground",
                !done && !laufend && "bg-muted text-muted-foreground"
              )}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : `0${i + 1}`}
            </span>
            <span
              className={cn(
                "truncate text-[11px] font-bold tracking-[0.06em] uppercase",
                laufend || done ? "text-foreground" : "text-muted-foreground/60"
              )}
            >
              {einheitDefinition(p).shortLabel}
            </span>
            {gezeigt && (
              <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary" />
            )}
          </>
        )

        const gemeinsam = cn(
          "relative flex min-w-0 items-center gap-3 px-4 py-3 text-left",
          i > 0 && "border-l border-[var(--rule)]",
          gezeigt && "bg-primary/[0.035]"
        )

        if (!anklickbar) {
          return (
            <div
              key={p}
              aria-current={gezeigt ? "step" : undefined}
              className={gemeinsam}
            >
              {inhalt}
            </div>
          )
        }

        return (
          <button
            key={p}
            type="button"
            onClick={() => onSelect(p)}
            title={
              laufend
                ? "Zurück ins laufende Gespräch"
                : `${EINHEIT_LABELS[p]} ansehen`
            }
            className={cn(
              gemeinsam,
              "cursor-pointer transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none focus-visible:ring-inset"
            )}
          >
            {inhalt}
          </button>
        )
      })}
    </div>
  )
}

// --- Cluster: stepper + current step description in one block ---
function StageHeader({
  run,
  description,
  action,
  status,
  viewing,
  imAbschluss,
  onSelect,
}: {
  run: ProcessRunDto
  description?: string
  action?: ReactNode
  status?: ReactNode
  viewing?: TrainingsEinheit
  imAbschluss?: boolean
  onSelect?: (unit: TrainingsEinheit) => void
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-[var(--rule)] bg-card/40">
      <ProgressRail
        run={run}
        viewing={viewing}
        imAbschluss={imAbschluss}
        onSelect={onSelect}
      />
      {(description || action || status) && (
        <div className="flex min-h-11 flex-col items-start gap-2 border-t border-[var(--rule)] px-4 py-2.5 sm:flex-row sm:items-center">
          {description && (
            <p className="min-w-0 flex-1 text-[13px] leading-relaxed text-muted-foreground sm:truncate">
              {description}
            </p>
          )}
          {status && <div className="shrink-0 sm:ml-auto">{status}</div>}
          {action && <div className="shrink-0 sm:ml-3">{action}</div>}
        </div>
      )}
    </div>
  )
}

/** Compact live IDD counter for the roleplay stage header. */
function IddTicker({
  activeSeconds,
  isIdle,
}: {
  activeSeconds: number
  isIdle: boolean
}) {
  return (
    <div
      className="flex items-center gap-1.5 text-[11.5px] font-bold tracking-[0.06em] text-muted-foreground uppercase"
      title="Angerechnete IDD-Weiterbildungszeit dieser Einheit"
    >
      <Clock className="h-3 w-3" />
      IDD
      <span
        className={cn(
          "tnum",
          isIdle ? "text-muted-foreground" : "text-foreground"
        )}
      >
        {formatHMS(activeSeconds)}
      </span>
      <span
        className={cn(
          "ml-0.5 inline-flex h-1.5 w-1.5 rounded-full",
          isIdle ? "bg-warning" : "animate-pulse bg-success"
        )}
        aria-hidden
      />
    </div>
  )
}

function errorMessage(error: unknown): string {
  const grund = apiFehlerDetail(error)
  if (grund) return grund
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message
  }
  return "Die Einheit konnte nicht abgeschlossen werden."
}

// --- Sticky bottom action bar ---
/**
 * Die rechte Spalte der zweispaltigen Etappen: Coach neben Bogen bzw. Bericht.
 *
 * Eine Konstante und nicht dreimal dieselbe Klassenzeile — sie stand schon in
 * drei Etappen identisch da, und ein Auseinanderlaufen wäre reiner Zufall.
 *
 * `lg:sticky`, damit der Coach beim Lesen des Gesamtbilds nicht nach oben
 * wegrutscht. Die Höhe kommt aus der Kopfhöhe des Shells statt aus einer
 * geratenen Zahl. `lg:min-h-0` ist tragend: sonst schlägt `min-h-[420px]` die
 * `height`, und auf einem kurzen Fenster wird das Panel höher als der
 * sichtbare Bereich. Unterhalb von `lg` bleibt es bei den 420px.
 */
const COACH_SPALTE =
  "flex min-h-[420px] min-w-0 flex-col overflow-hidden rounded-lg border border-[var(--rule)] bg-card lg:sticky lg:top-0 lg:h-[calc(100dvh-var(--app-header-height)-6rem)] lg:min-h-0"

/**
 * Die Vollbild-Umschaltung der Coach-Spalte.
 *
 * Unter `lg` steht ohnehin nur eine Spalte, dort gibt es nichts zu erweitern —
 * deshalb hängt der Zustand am Breitenmerkmal und nicht nur am Knopf.
 */
function useChatVollbild() {
  const breit = useMediaQuery("(min-width: 1024px)")
  const [gewuenscht, setGewuenscht] = useState(false)
  return {
    breit,
    voll: gewuenscht && breit,
    umschalten: () => setGewuenscht((v) => !v),
  }
}

/**
 * Das Raster einer zweispaltigen Etappe.
 *
 * Die Spalten wandern ineinander, statt umzuspringen — wie im Feedback. Beide
 * Zustände sind dieselbe Form (zwei `minmax`-Spuren), sonst gäbe es nichts zu
 * interpolieren.
 */
function etappenRaster(voll: boolean) {
  return cn(
    "grid gap-4 transition-[grid-template-columns,gap] duration-300 ease-out motion-reduce:transition-none",
    voll
      ? // Ohne `gap-0` bliebe der Spaltenabstand vor der auf null gelaufenen
        // Spur stehen, und das Gespräch stünde 16px weiter rechts als die
        // Leiste darüber. Der Abstand läuft mit, sonst wäre der Sprung sichtbar.
        "lg:grid-cols-[minmax(0,0fr)_minmax(0,100%)] lg:gap-0"
      : "lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)]"
  )
}

/**
 * Die linke Spalte, die dem Gespräch weichen kann.
 *
 * Eingeklappt läuft sie auf null Breite, statt zu verschwinden: `display:none`
 * wäre ein Sprung statt einer Bewegung. Gemountet bleibt sie, damit nichts neu
 * lädt, und `inert` hält Fokus und Vorlesen draußen, solange sie unsichtbar ist.
 */
function EtappenSpalte({
  voll,
  children,
}: {
  voll: boolean
  children: ReactNode
}) {
  return (
    <div
      inert={voll}
      className={cn(
        "flex min-h-0 min-w-0 flex-col gap-4 transition-opacity duration-200 motion-reduce:transition-none",
        voll && "overflow-hidden opacity-0"
      )}
    >
      {children}
    </div>
  )
}

/**
 * Die Coach-Spalte mit ihrem Kopfbalken.
 *
 * Derselbe Balken und dasselbe Symbolpaar wie am Feedback-Blatt, nur
 * andersherum: dort weicht das Gespräch dem Blatt, hier das Blatt dem Gespräch.
 * Kein eigenes Fenster — in einem Dialog wäre der Verlauf vom Durchlauf
 * abgeschnitten, und genau der Zusammenhang ist der Grund, warum er hier steht.
 */
function CoachSpalte({
  voll,
  breit,
  onUmschalten,
  children,
}: {
  voll: boolean
  breit: boolean
  onUmschalten: () => void
  children: ReactNode
}) {
  return (
    <div className={COACH_SPALTE}>
      <div className="no-print flex shrink-0 items-center justify-between gap-3 border-b border-[var(--rule)] px-4 py-2.5">
        <span className="text-[11.5px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
          Gespräch
        </span>
        {breit && (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={onUmschalten}
            aria-label={voll ? "Gespräch verkleinern" : "Gespräch vergrößern"}
            className="h-7 w-7"
          >
            {voll ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  )
}

/**
 * Die Abschlussleiste einer Etappe, unten klebend.
 *
 * Absichtlich unten knapper gepolstert als oben: unter dem Scroll-Bereich
 * liegt bereits die `pb-4` des Seitenrahmens. Symmetrische Polsterung stapelte
 * sich mit ihr, und unter dem Knopf stand doppelt so viel Luft wie darüber.
 *
 * Auch kein `min-h-[69px]` wie die Leisten im Konfigurator: die tragen zwei
 * Knöpfe und eine Rückwärts-Navigation, hier steht einer. Die Überhöhe war
 * reine Luft, und sie landete zur Hälfte unten.
 */
function StickyAction({ children }: { children: ReactNode }) {
  return (
    <div className="sticky bottom-0 z-20 mt-4 flex items-center justify-end border-t border-[var(--rule)] bg-[var(--paper)]/90 pt-3 pb-1 backdrop-blur">
      {children}
    </div>
  )
}

/** A roleplay stage (Terminierung or Beratungsgespräch). */
function StageRoleplay({
  run,
  session,
  persona,
  onAdvanced,
  onSelectUnit,
}: {
  run: ProcessRunDto
  session: TrainingSessionDto
  persona: Persona
  /** `mitFeedback: false` skips the unit's review and goes straight on. */
  onAdvanced: (
    completedSessionId: string,
    unit: TrainingsEinheit,
    mitFeedback: boolean
  ) => void
  onSelectUnit: (unit: TrainingsEinheit) => void
}) {
  const advance = useAdvanceStage(run.id)
  const qc = useQueryClient()
  const [ending, setEnding] = useState(false)
  const [finishFailed, setFinishFailed] = useState(false)
  // Sichert den Verlauf und wartet, bis er liegt. Gefüllt von RoleplayChat: nur
  // der Chat weiß, was schon geschrieben ist. Der frühere Nachtrag hier zählte
  // gegen die Sitzungsabfrage, und die ist nach dem laufenden Schreiben veraltet
  // — er hätte alles ein zweites Mal geschrieben.
  const flushChat = useRef<(() => Promise<void>) | null>(null)
  // Gefüllt von RoleplayChat; „Ergebnis zeigen" schickt darüber seinen Zug.
  const aktionImChat = useRef<((text: string) => void) | null>(null)

  // IDD training time for this phase. The client counts active seconds; a
  // heartbeat anchors them server-side (monotonic), so the record survives a
  // reload or a closed tab mid-phase.
  const time = useActiveTrainingTime(!ending)
  const timeRef = useRef(time)
  timeRef.current = time

  const sendHeartbeat = () =>
    sessionsControllerHeartbeat({
      path: { id: session.id },
      body: {
        activeSeconds: Math.round(timeRef.current.activeSeconds),
        idleSeconds: Math.round(timeRef.current.idleSeconds),
      },
    }).catch(() => undefined)

  useEffect(() => {
    const handle = window.setInterval(() => void sendHeartbeat(), 15000)
    return () => {
      window.clearInterval(handle)
      // Persist whatever accrued before leaving the phase.
      void sendHeartbeat()
    }
  }, [session.id])

  const initial: UIMessage[] = useMemo(
    () =>
      session.messages.map((m, i) => ({
        id: `${i}-${m.role}`,
        role: m.role,
        parts: [{ type: "text", text: m.content }],
      })),
    [session.messages]
  )
  const latest = useRef<UIMessage[]>(initial)
  // Counts settled turns for the IDD check, which reads the persisted
  // transcript: so wait until the stream is quiet, then until the chat has
  // written it. Checking earlier judged the turn before last.
  const [zug, setZug] = useState(0)
  const ruheTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const verlaufStand = useRef("")
  const zugNachRuhe = (messages: UIMessage[]) => {
    // The callback fires on every render (the timer re-renders each second),
    // so only a changed transcript may restart the wait.
    const letzte = messages.at(-1)
    const stand = `${messages.length}:${letzte ? JSON.stringify(letzte.parts).length : 0}`
    if (stand === verlaufStand.current) return
    verlaufStand.current = stand
    if (ruheTimer.current) clearTimeout(ruheTimer.current)
    ruheTimer.current = setTimeout(() => {
      void (flushChat.current?.() ?? Promise.resolve()).then(() =>
        setZug((z) => z + 1)
      )
    }, 1500)
  }
  useEffect(() => () => {
    if (ruheTimer.current) clearTimeout(ruheTimer.current)
  }, [])

  const finish = async (mitFeedback = true) => {
    if (ending) return
    if (latest.current.filter((m) => m.role === "user").length < 1) {
      toast.error(
        "Bitte sende mindestens eine Nachricht, bevor du die Einheit abschließt."
      )
      return
    }
    setEnding(true)
    setFinishFailed(false)
    try {
      // Was noch offen ist, schreibt der Chat selbst — und wartet darauf.
      await flushChat.current?.()
      // Persist the final IDD time for this phase before it advances.
      await sendHeartbeat()
      // A previous attempt may have timed out *after* the server already
      // advanced the run (advance extracts case facts with a model call and
      // can be slow) — check the fresh state so a retry never advances twice.
      const { data: fresh } = await processRunControllerGet({
        path: { id: run.id },
      })
      const alreadyAdvanced =
        !!fresh &&
        (fresh.status !== "active" || fresh.currentUnit !== session.unit)
      if (alreadyAdvanced) {
        void qc.invalidateQueries({
          queryKey: processRunControllerGetQueryKey({ path: { id: run.id } }),
        })
      } else {
        await advance.mutateAsync(run.id)
      }
      onAdvanced(session.id, einheit, mitFeedback)
    } catch (error) {
      setFinishFailed(true)
      toast.error(errorMessage(error))
      setEnding(false)
    }
  }

  const einheit = session.unit as TrainingsEinheit
  const blattStand = arbeitsblattStand(einheit, run.caseState)
  // On the phone the advisor does not see her counterpart — neither in the
  // transcript nor in the live call. The picture would take the difficulty away.
  const amTelefon = einheitDefinition(einheit).kanal === "telefon"
  const vorgeschichte = einheitenVorgeschichte(
    einheit,
    run.units as TrainingsEinheit[],
    run.bausteine
  )
  // The same appointment going on: the unit before is shown above the new
  // turns and known to the persona (the server loads it itself).
  const fortsetzung = fortsetzungVon(einheit, run.units)
  const vorige = fortsetzung ? sessionForUnit(run, fortsetzung) : undefined
  const vorgespraech = useMemo(
    () =>
      vorige?.messages.length && fortsetzung
        ? {
            label: einheitDefinition(fortsetzung).shortLabel,
            messages: vorige.messages.map((m) => ({
              role: m.role,
              content: m.content,
            })),
          }
        : undefined,
    [vorige, fortsetzung]
  )
  const [blattOffen, setBlattOffen] = useState(false)
  const [werkzeugOffen, setWerkzeugOffen] = useState(false)
  const [infoOffen, setInfoOffen] = useState(false)
  // Units with a guide get a checklist instead of the case sheet; the sheet's
  // content moves behind the info button on the customer header.
  const leitfaden = leitfadenFuer(einheit, persona)
  const [haken, umschalteHaken] = useChecklistenHaken(session.id)
  // Geschrieben wird nur in der Einheit, die die Bedarfsanalyse trainiert.
  // Danach bleibt das Werkzeug lesbar — der Endpunkt wiese einen Schreibversuch
  // ohnehin ab, und ein Feld, das sich ändern lässt und dann nicht gespeichert
  // wird, wäre die schlechtere Antwort darauf.
  const werkzeugSchreibbar =
    phasenDerEinheit(einheit).includes("bedarfsanalyse")
  const saveAvTool = useSaveAvTool(run.id)
  // Stabil über `useCallback`, weil das Werkzeug entprellt speichert: eine bei
  // jedem Zug neue Funktion setzte den Timer im Fenster ständig zurück, und
  // gespeichert würde nie.
  const speichereAvTool = useCallback(
    (eingabe: AvToolEingabe, zeigen: boolean) => {
      saveAvTool.mutate(
        { eingabe, zeigen },
        {
          // Den Grund durchreichen und nicht verschlucken: eine Verletzung
          // des Schemas trägt ihn in `issues`, ein abgewiesener Schreibversuch
          // in der Meldung. Ohne das stünde nur da, DASS es klemmt.
          onError: (fehler) => {
            const grund = apiFehlerDetail(fehler)
            toast.error(
              grund
                ? `AV-Tool: ${grund} Deine Eingaben bleiben im Fenster.`
                : "Der Stand des AV-Tools konnte nicht gespeichert werden. Deine Eingaben bleiben im Fenster."
            )
          },
        }
      )
      // Zeigen ist eine Handlung im Gespräch, keine stille Schreiboperation:
      // die Zeile geht als Zug in den Verlauf, und weil es ein Zug ist,
      // antwortet die Kundin darauf. Im Sprachanruf übernimmt das der Server
      // (der Chat ist dann ohnehin gesperrt).
      if (zeigen) {
        aktionImChat.current?.(
          aktionsZeile(avToolGezeigtText(berechneAvTool(eingabe)))
        )
      }
    },
    [saveAvTool]
  )

  // The solution unit replaces the case sheet with the solution balance: the
  // facts were gathered in the unit before, what matters now is the fit.
  const imLoesungsteil = phasenDerEinheit(einheit).includes("loesung_abschluss")
  const saveLoesung = useSaveLoesung(run.id)
  const [loesungBreit, setLoesungBreit] = useState(false)
  const speichereLoesung = useCallback(
    (vorschlaege: LoesungVorschlag[], zeigen: boolean) => {
      saveLoesung.mutate(
        { vorschlaege, zeigen },
        {
          onError: (fehler) => {
            const grund = apiFehlerDetail(fehler)
            toast.error(
              grund
                ? `Lösungsvorschlag: ${grund}`
                : "Der Lösungsvorschlag konnte nicht gespeichert werden."
            )
          },
        }
      )
    },
    [saveLoesung]
  )
  // Text chat only; in a live call the server injects the same line.
  const zeigeLoesungImChat = useCallback(
    (text: string) => aktionImChat.current?.(aktionsZeile(text)),
    []
  )
  const loesungPanel = (props: { action?: ReactNode; className?: string }) => (
    <LoesungPanel
      caseState={run.caseState}
      fall={persona.fall}
      onSpeichern={speichereLoesung}
      onZeigen={zeigeLoesungImChat}
      breit={loesungBreit}
      onBreite={() => setLoesungBreit((b) => !b)}
      {...props}
    />
  )

  // One definition, two homes: docked at the sheet's foot on desktop, in the
  // row under the rail on narrow screens. The failure state must follow the
  // button wherever it renders.
  const abschlussKnopf = (props: { size: "sm" | "lg"; className?: string }) =>
    finishFailed ? (
      <Button
        onClick={() => void finish()}
        variant="destructive"
        size={props.size}
        className={props.className}
      >
        <RefreshCw className="h-4 w-4" />
        Erneut versuchen
      </Button>
    ) : (
      <Button
        onClick={() => void finish()}
        disabled={ending}
        size={props.size}
        className={props.className}
      >
        {ending && <Loader2 className="h-4 w-4 animate-spin" />}
        {einheitDefinition(einheit).shortLabel} abschließen
        <ArrowRight className="h-4 w-4" />
      </Button>
    )

  const naechsteEinheit = run.units[run.units.indexOf(einheit) + 1] as
    | TrainingsEinheit
    | undefined
  const bedarfErledigt = !!run.caseState.avTool?.gezeigtAm
  const leitfadenAktionen = leitfaden && (
    <PhasenAktionen
      freigegeben={
        leitfaden.freigabe === "av_tool"
          ? bedarfErledigt
          : checklisteErledigt(leitfaden, haken)
      }
      hinweis={freigabeHinweis(leitfaden, persona)}
      laeuft={ending}
      fehlgeschlagen={finishFailed}
      onFeedback={() => void finish(true)}
      onWeiter={naechsteEinheit ? () => void finish(false) : undefined}
    />
  )
  const leitfadenPanel = (className?: string) =>
    leitfaden && (
      <PhasenLeitfaden
        leitfaden={leitfaden}
        haken={haken}
        onHaken={umschalteHaken}
        bedarfErledigt={bedarfErledigt}
        onWerkzeug={
          leitfaden.bedarfsanalyse
            ? () => {
                setBlattOffen(false)
                setWerkzeugOffen(true)
              }
            : undefined
        }
        aktionen={leitfadenAktionen}
        className={className}
      />
    )

  return (
    <div className="flex h-full min-h-0 flex-col">
      <StageHeader
        run={run}
        viewing={run.currentUnit as TrainingsEinheit | undefined}
        onSelect={onSelectUnit}
        status={
          SHOW_IDD_TIME ? (
            <IddTicker
              activeSeconds={time.activeSeconds}
              isIdle={time.isIdle}
            />
          ) : undefined
        }
      />

      {/* Unter der Zweispaltenbreite gibt es weder die Blatt-Spalte noch deren
          Fuß: Griff zum Blatt und Einheitenabschluss stehen deshalb in einer
          eigenen Zeile. Auf breiten Schirmen trägt beides das Blatt. */}
      <div className="flex shrink-0 flex-col items-stretch gap-2 pt-2 sm:flex-row sm:items-center sm:justify-end lg:hidden">
        {/* Der Griff zum Blatt in jeder Rollenspiel-Einheit: in der
            Terminierung hing sonst auch der Anlass mit drin und war auf dem
            Handy nicht erreichbar. Das Werkzeug bleibt an der Bedarfsanalyse. */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setBlattOffen(true)}
          className="gap-1.5"
        >
          <ClipboardList className="h-3.5 w-3.5" />
          {imLoesungsteil
            ? "Lösungsvorschlag"
            : einheitDefinition(einheit).shortLabel}
          {leitfaden ? (
            <span className="rounded-full bg-secondary px-1.5 font-mono text-[11px] tabular-nums">
              {
                leitfaden.checkliste.punkte.filter((p) => haken.has(p.key))
                  .length
              }
              /{leitfaden.checkliste.punkte.length}
            </span>
          ) : (
            !imLoesungsteil && (
              <span className="rounded-full bg-secondary px-1.5 font-mono text-[11px] tabular-nums">
                {blattStand.erfasst}/{blattStand.gesamt}
              </span>
            )
          )}
        </Button>
        {phasenDerEinheit(einheit).includes("bedarfsanalyse") && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setWerkzeugOffen(true)}
            className="gap-1.5"
          >
            <Calculator className="h-3.5 w-3.5" />
            AV-Tool
          </Button>
        )}
        {/* The two gated actions live in the sheet: side by side with the
            other handles they would not fit a phone's width. */}
        {!leitfaden && abschlussKnopf({ size: "sm" })}
      </div>

      {/* Das Werkzeug nimmt breit die rechte Spur ein, statt über ihr zu
          schweben. Ein schwebendes Fenster überdeckte den unteren Rand der
          Chatspalte — und dort sitzt bei laufendem Anruf die Leiste mit
          Auflegen. Als Spur verschiebt sich der Chat stattdessen, nichts liegt
          übereinander, und weil nur `grid-template-columns` wechselt, wird
          nichts neu eingehängt: der Anruf läuft weiter.

          `relative` bleibt für die schmale Darstellung, wo das Werkzeug als
          Vollfläche darüberliegt. */}
      <div
        className={cn(
          "relative mt-3 grid min-h-0 flex-1 gap-3 transition-[grid-template-columns] duration-200",
          werkzeugOffen || (imLoesungsteil && loesungBreit)
            ? "lg:grid-cols-[minmax(0,1fr)_600px]"
            : imLoesungsteil
              ? "lg:grid-cols-[minmax(0,1fr)_400px]"
              : "lg:grid-cols-[minmax(0,1fr)_340px]"
        )}
      >
        <div className="flex min-h-0 flex-col gap-2.5">
          {/* Wer da spricht, steht über dem Gespräch — nicht mehr als Insel im
              leeren Verlauf, die mit der ersten Nachricht verschwand. Am Telefon
              bleibt die Karte da, aber ohne Gesicht: der Name steht im Display,
              das Bild hätte die Beraterin nicht. */}
          <div className="flex shrink-0 items-center gap-3 rounded-lg border border-[var(--rule)] bg-card px-3.5 py-2.5">
            <Avatar className="size-11 ring-4 ring-accent/40">
              {persona.portraitUrl && !amTelefon ? (
                <AvatarImage src={persona.portraitUrl} alt="" />
              ) : null}
              <AvatarFallback className="bg-accent text-[14px] font-medium text-primary">
                {initials(persona.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-[15px] leading-tight font-bold">
                {persona.name}
              </p>
              <p className="text-[12.5px] text-muted-foreground">
                {amTelefon ? "am Telefon" : persona.profession}
              </p>
            </div>
            {leitfaden && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setInfoOffen(true)}
                aria-label={`Infos zu ${persona.name}`}
                className="ml-auto text-primary hover:text-primary"
              >
                <Info className="h-5 w-5" />
              </Button>
            )}
          </div>
          {/* Ohne eigenen Rahmen: die Chatkarte war eine dritte Ebene zwischen
              Panel und Bubble und hat nur Höhe gekostet. Die Trennung zum
              Arbeitsblatt tragen dessen eigener Rahmen und der Spaltenspalt. */}
          <div className="min-h-0 flex-1 overflow-hidden">
            <RoleplayChat
              sessionId={session.id}
              persona={persona}
              vorgeschichte={vorgeschichte}
              vorgespraech={vorgespraech}
              einheitLabel={einheitDefinition(einheit).shortLabel}
              amTelefon={amTelefon}
              initialMessages={initial}
              flushRef={flushChat}
              aktionRef={aktionImChat}
              onMessagesChange={(messages) => {
                latest.current = messages
                zugNachRuhe(messages)
              }}
              disabled={ending}
              compact
              // Once per run: the notice belongs to the start of the
              // simulation, not to every unit.
              beispielwerteHinweisKey={`nl:beispielwerte-hinweis:${run.id}`}
            />
          </div>
        </div>
        {/* The working sheet that grows from facts established in the call,
            with the phase action docked at its foot, level with the composer. */}
        {/* Das Blatt weicht dem Werkzeug: es zeigt dieselben Zahlen, nur
            knapper. Beides nebeneinander wäre eine Dopplung. */}
        <div
          className={cn(
            "hidden min-h-0 flex-col gap-3",
            !werkzeugOffen && "lg:flex"
          )}
        >
          <IddRunPanel runId={run.id} zug={zug} className="shrink-0" />
          <div className="min-h-0 flex-1">
          {imLoesungsteil ? (
            loesungPanel({
              action: abschlussKnopf({ size: "lg", className: "w-full" }),
            })
          ) : leitfaden ? (
            leitfadenPanel()
          ) : (
            <Arbeitsblatt
              run={run}
              persona={persona}
              vorgeschichte={vorgeschichte}
              einheit={einheit}
              // In „Lösung & Abschluss" ist das Werkzeug lesbar, aber nicht mehr
              // zu ändern: die Zahlen, auf denen ihre Lösung steht, bleiben
              // sichtbar, erarbeitet wurden sie aber in der Einheit davor.
              onWerkzeug={
                phasenDerEinheit(einheit).includes("bedarfsanalyse") ||
                run.caseState.avTool
                  ? () => setWerkzeugOffen(true)
                  : undefined
              }
              action={abschlussKnopf({ size: "lg", className: "w-full" })}
            />
          )}
          </div>
        </div>

        {/* Das AV-Tool als Geschwister der Rasterspalten, nicht als Dialog.
            Ein Radix-Modal überlebte den Anruf zwar (es portalt nach body),
            finge aber den Fokus und legte alles dahinter auf
            `pointer-events: none` — Auflegen und Chatzeile wären tot. So liegt
            es nur über dem Arbeitsblatt und einem Teil des Verlaufs, und beides
            daneben bleibt bedienbar.

            600 px: der Durchlauf deckelt bei `max-w-6xl`, abzüglich Polsterung
            bleiben rund 1104 px. Das Fenster verdeckt damit das 340-px-Blatt
            ganz und lässt gut 500 px Verlauf stehen. */}
        {werkzeugOffen && (
          <AvToolFenster
            gespeichert={run.caseState.avTool}
            persona={persona}
            onClose={() => setWerkzeugOffen(false)}
            onSpeichern={werkzeugSchreibbar ? speichereAvTool : undefined}
            readOnly={!werkzeugSchreibbar}
            // Desktop only: below lg the finish button stays in the row
            // above, which the tool does not cover.
            action={
              <div className="hidden shrink-0 border-t border-[var(--rule)] p-3 lg:block">
                {leitfaden
                  ? leitfadenAktionen
                  : abschlussKnopf({ size: "lg", className: "w-full" })}
              </div>
            }
            // Schmal legt es sich über den Verlauf, breit wird es zur rechten
            // Rasterspur. Bewusst kein `Sheet`: dessen Overlay liegt
            // `fixed inset-0` über dem ganzen Schirm, fängt den Fokus und
            // schluckt jeden Klick — Chat und Auflegen wären unerreichbar.
            className="absolute inset-0 z-20 lg:static lg:z-auto"
          />
        )}
      </div>

      {/* Unter der Zweispaltenbreite war das Arbeitsblatt bisher schlicht
          ausgeblendet: die Gesprächsdaten waren dort nicht erreichbar. Als
          eingezogenes Blatt mit Zähler am Griff bleiben sie es. */}
      <Sheet open={blattOffen} onOpenChange={setBlattOffen}>
        <SheetContent
          side="bottom"
          className="max-h-[80vh] overflow-y-auto lg:hidden"
        >
          <SheetHeader>
            <SheetTitle>
              {imLoesungsteil
                ? "Lösungsvorschlag"
                : einheitDefinition(einheit).shortLabel}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-3 px-4 pb-6">
            {/* Display only: the column copy stays mounted while hidden and
                already triggers the check; two would judge every turn twice. */}
            <IddRunPanel runId={run.id} zug={0} />
            {imLoesungsteil ? (
              loesungPanel({ className: "h-auto overflow-visible" })
            ) : leitfaden ? (
              leitfadenPanel("h-auto overflow-visible")
            ) : (
              <Arbeitsblatt
                run={run}
                persona={persona}
                vorgeschichte={vorgeschichte}
                einheit={einheit}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      <PersonaInfoDialog
        open={infoOffen}
        onOpenChange={setInfoOffen}
        persona={persona}
        amTelefon={amTelefon}
        felder={arbeitsblattFelder(einheit)}
        caseState={run.caseState}
        vorgeschichte={vorgeschichte}
      />
    </div>
  )
}

/**
 * Maps a free-text fact label from the phone call onto a master-data key, so a
 * fact like »Kinder« fills the »Kinder« field instead of sitting redundantly in
 * the gathered-facts list. Returns undefined for facts without a home (wishes,
 * concerns, availability …), which stay in the "Aus dem Telefonat" list.
 */
function matchStammdatenKey(rawLabel: string): string | undefined {
  const label = rawLabel.toLowerCase()
  const rules: [RegExp, string][] = [
    [/kind/, "kinder"],
    [
      /familienstand|verheirat|ledig|geschieden|verwitwet|ehe\b/,
      "familienstand",
    ],
    [
      /beruf|arbeitgeber|tätig|angestellt|selbstständig|selbststaendig/,
      "beruf",
    ],
    [/einkommen|gehalt|netto|verdien|lohn/, "einkommen"],
    [/bestand|vorsorge|depot|anlage|vertrag|versicherung/, "bestandsvorsorge"],
    [/wohn|miet|eigentum|immobil|\bhaus\b|wohnung/, "wohnsituation"],
    [/\balter\b|jahre alt/, "alter"],
  ]
  for (const [re, key] of rules) if (re.test(label)) return key
  return undefined
}

/**
 * The preparation checkpoint: review and edit the automatically collected data
 * — master data plus facts gathered on the phone — and set a goal. Every entry
 * is an editable field (the advisor's prep sheet), not a checkbox.
 */
/**
 * The leading questions of the preparation.
 *
 * They sit visibly above the sheet because the unit is not "fill in fields" but
 * "draw conclusions". The coach asks the same questions in the dialogue beside
 * it; both sides read them from the same place.
 */
/** Spelled out, so the number in prose can never drift from the table. */
function leitfragenAnzahl(einheit: TrainingsEinheit): string {
  const zahlwort = ["keine", "eine", "zwei", "drei", "vier", "fünf", "sechs"]
  const n = einheitDefinition(einheit).leitfragen.length
  return zahlwort[n] ?? String(n)
}

function Leitfragen({ einheit }: { einheit: TrainingsEinheit }) {
  const fragen = einheitDefinition(einheit).leitfragen
  if (!fragen.length) return null

  return (
    <Card className="gap-0 border-[var(--rule)] p-5">
      <div className="mb-2 text-[11px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
        Leitfragen
      </div>
      <ol className="space-y-2">
        {fragen.map((frage, i) => (
          <li key={frage} className="flex gap-2.5 text-[14px] leading-relaxed">
            <span className="mt-0.5 shrink-0 font-mono text-[11px] text-muted-foreground tabular-nums">
              0{i + 1}
            </span>
            <span className="text-foreground/85">{frage}</span>
          </li>
        ))}
      </ol>
    </Card>
  )
}

/**
 * The sparring beside a sheet: the coach walks the advisor through the leading
 * questions, written or spoken. Used by both sparring units.
 *
 * No reflection conversation and no feedback — the unit's step ends when the
 * sheet is submitted, not with a judgement. The server recognises which of the
 * two it is from the session's unit and builds the matching brief; what stands
 * here is only the thread and the words around it.
 *
 * The call takes this panel over instead of opening a dialog: the sheet and the
 * leading questions stay visible beside it, and they are what the conversation
 * is about — a modal would cover exactly what she is supposed to fill in.
 */
function PrepSparring({
  runId,
  sessionId,
  coachStyleId,
  persona,
  initialMessages,
  art,
  empty,
}: {
  /** Which sparring — colours the call screen and the greeting. */
  art: "vorbereitung" | "nachbereitung"
  /** Eyebrow, title and hint of the empty thread. */
  empty: { eyebrow: string; title: string; hint: (coachName: string) => string }
  /** Nur zum Nachladen: die Turns des Anrufs hängen am Durchlauf. */
  runId: string
  sessionId: string
  coachStyleId?: string
  /**
   * Ohne sie kein Anruf: der Anrufschirm sagt, um wessen Termin es geht.
   * Fehlt sie (die Personenliste lädt noch), bleibt das Schreiben.
   */
  persona?: Persona
  initialMessages?: TrainingSessionDto["coachChat"]
}) {
  const qc = useQueryClient()
  const coach = useCoachFor(coachStyleId)
  const coachName = coach?.name ?? COACH_FALLBACK_LABEL
  const [voiceOpen, setVoiceOpen] = useState(false)
  // Der Anruf wird serverseitig in denselben Verlauf geschrieben. Sichtbar wird
  // er erst durch einen Neuaufbau, weil `useChat` seinen Thread beim Mount
  // seedet und eine nachgeladene Nachricht nicht mehr annimmt.
  const [epoche, setEpoche] = useState(0)

  const anrufKnopf = persona ? (
    <Button
      type="button"
      variant="secondary"
      onClick={() => setVoiceOpen(true)}
    >
      <Phone className="h-4 w-4" />
      Leitfragen mit {coachName} durchgehen
    </Button>
  ) : null

  // Läuft der Anruf, IST er die Box. Der Verlauf wird danach ohnehin neu
  // aufgebaut, weil die Turns serverseitig hineingeschrieben werden.
  if (persona && voiceOpen) {
    return (
      // Scrollbar statt beschnitten: die Box hat eine feste Höhe, und in einem
      // niedrigen Fenster läge der Auflegen-Knopf sonst darunter. Die Leiste des
      // Panels klebt unten, bleibt also auch beim Scrollen sichtbar.
      <div className="h-full overflow-y-auto">
        <VoiceSessionPanel
          className="min-h-full justify-between"
          target={{
            kind: "persona",
            sessionId,
            persona,
            mode: "coach",
            coach,
            sparringArt: art,
          }}
          onEnd={(turns) => {
            setVoiceOpen(false)
            if (turns.length === 0) return
            void (async () => {
              // Erst laden, dann neu aufbauen: ein früherer Wechsel der Epoche
              // spielte den Verlauf ohne den Anruf wieder ein.
              await qc.invalidateQueries({
                queryKey: processRunControllerGetQueryKey({
                  path: { id: runId },
                }),
              })
              setEpoche((n) => n + 1)
            })()
          }}
        />
      </div>
    )
  }

  return (
    <CoachThread
      key={epoche}
      scope={{ kind: "session", sessionId }}
      coachStyleId={coachStyleId}
      extraBody={() => ({ sessionId })}
      initialMessages={initialMessages}
      scroll="inner"
      // Sits in its own panel beside the sheet, without page margins of its own.
      inset
      empty={{
        hero: {
          portraitUrl: coach?.portraitUrl,
          name: coachName,
          role: coach?.headline ?? "Dein Coach",
        },
        eyebrow: empty.eyebrow,
        title: empty.title,
        hint: empty.hint(coachName),
        actions: anrufKnopf,
      }}
      placeholder={{ ready: `Schreib ${coachName}, was dir auffällt …` }}
      // Sobald der Begrüßungsblock weg ist, bleibt der Anruf hier erreichbar.
      composerAction={
        persona ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => setVoiceOpen(true)}
                aria-label={`${coachName} anrufen`}
                className="shrink-0 rounded-md text-muted-foreground hover:text-foreground"
              >
                <Phone className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{coachCallHint(coachName)}</TooltipContent>
          </Tooltip>
        ) : undefined
      }
    />
  )
}

/**
 * One field of the prep sheet: label above, value below.
 *
 * Both come from the case data and can be arbitrarily long (»Bestandsverträge /
 * Anlagen / Depot«, a full employer name). Side by side they pushed each other
 * out of the box and the wrapping turned the sheet ragged; stacked, each gets
 * the full column and the fields stay on a common grid. The value is a textarea
 * and not an input because an input scrolls long text out of sight instead of
 * wrapping it, and a clipped field hides exactly the data to be checked.
 */
function PrepFeld({
  label,
  value,
  onChange,
  readOnly,
  variant = "stamm",
  marker,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  readOnly?: boolean
  variant?: "stamm" | "telefonat"
  marker?: string
}) {
  const ref = useRef<HTMLTextAreaElement>(null)

  // No scrollbar: the field is resized to its content on every change and
  // whenever the column gets narrower (breakpoint, sparring panel beside it).
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const fit = () => {
      el.style.height = "auto"
      el.style.height = `${el.scrollHeight}px`
    }
    fit()
    // Only the width matters; reacting to the height as well would answer our
    // own resize and provoke the ResizeObserver loop warning.
    let width = el.clientWidth
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === width) return
      width = el.clientWidth
      fit()
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [value])

  return (
    <label
      className={cn(
        "flex min-w-0 flex-col gap-0.5 rounded-md border px-2.5 py-2 transition-colors focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/25",
        variant === "telefonat"
          ? "border-primary/30 bg-primary/[0.04]"
          : "border-[var(--rule)] bg-background/50"
      )}
    >
      <span className="flex items-baseline gap-2 text-[12px] leading-[1.3] text-muted-foreground">
        <span className="min-w-0 break-words">{label}</span>
        {marker && (
          <span className="ml-auto shrink-0 text-[10.5px] font-bold tracking-[0.06em] text-primary/70 uppercase">
            {marker}
          </span>
        )}
      </span>
      <textarea
        ref={ref}
        rows={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        // A field holds one statement; a line break would only push the sheet
        // apart without adding anything.
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault()
        }}
        readOnly={readOnly}
        placeholder="offen"
        className="w-full resize-none overflow-hidden bg-transparent text-[14px] leading-[1.45] font-medium outline-none placeholder:font-normal placeholder:text-muted-foreground/50"
      />
    </label>
  )
}

/**
 * The preparation checkpoint. Read-only in retrospect: the data moved into the
 * case state on submission, and editing it afterwards would put the following
 * units on a basis that never existed in the conversation.
 */
function StagePrep({
  run,
  persona,
  readOnly = false,
}: {
  run: ProcessRunDto
  /** Für den Anruf im Sparring; fehlt sie, bleibt es beim Schreiben. */
  persona?: Persona
  readOnly?: boolean
}) {
  const sparringSession = sessionForUnit(run, "gespraechsvorbereitung")
  const vollbild = useChatVollbild()
  const submit = useSubmitPreparation(run.id)
  const [goal, setGoal] = useState(() => run.caseState.preparation?.goal ?? "")

  const truth = run.caseState.personaTruth ?? {}
  const gathered = run.caseState.gatheredInfo ?? []

  const baseRows: { key: string; label: string; value?: string }[] = [
    {
      key: "alter",
      label: "Alter",
      value: truth.alter != null ? String(truth.alter) : undefined,
    },
    {
      key: "familienstand",
      label: "Familienstand",
      value: truth.familienstand,
    },
    { key: "kinder", label: "Kinder", value: truth.kinder },
    { key: "beruf", label: "Beruf, Arbeitgeber", value: truth.beruf },
    {
      key: "einkommen",
      label: "Einkommen",
      value:
        truth.einkommenNetto != null ? `${truth.einkommenNetto} €` : undefined,
    },
    {
      key: "bestandsvorsorge",
      label: "Bestandsverträge / Anlagen / Depot",
      value: truth.bestandsvorsorge,
    },
    {
      key: "wohnsituation",
      label: "Wohnsituation",
      value: truth.wohnsituation,
    },
  ]

  // Pflichtzeile des Bausteins Einkommensschutz (Handout Gesprächsvorbereitung).
  // Der Wert ist ein Vorschlag, keine Berechnung: 90 % vom Netto ist Variante 1
  // des Handouts, Variante 2 wäre ein fester Betrag. Eingetragen wird von Hand,
  // wie in jeder anderen Zeile auch.
  //
  // Unbedingt sichtbar, weil der Baustein heute für alle aktiv ist. Eine Abfrage
  // beim Mandanten ist hier ohnehin nicht möglich: Berater dürfen `Tenant` nicht
  // lesen, dafür bräuchte es erst einen eigenen Weg an die aktiven Bausteine.
  if (run.bausteine.includes("einkommensschutz")) {
    const netto = truth.einkommenNetto
    baseRows.push({
      key: "eks_vorabberechnung",
      label: "Einkommensschutz: Vorab-Berechnung",
      value:
        netto != null
          ? `${Math.round(netto * 0.9)} € (90 % vom Netto)`
          : undefined,
    })
  }

  // Facts learned on the phone that map onto a master-data field fill that field
  // (single source of truth) and are removed from the "Aus dem Telefonat" list
  // below, so the same info never appears twice and never contradicts itself.
  const factByKey = new Map<string, string>()
  const consumed = new Set<number>()
  gathered.forEach((g, i) => {
    const key = matchStammdatenKey(g.label)
    if (!key) return
    consumed.add(i)
    if (!factByKey.has(key)) factByKey.set(key, g.value)
  })

  const rows = baseRows.map((r) => {
    if (r.value) return { ...r, fromPhone: false }
    const fact = factByKey.get(r.key)
    return { ...r, value: fact, fromPhone: fact != null }
  })

  const remainingGathered = gathered.filter((_, i) => !consumed.has(i))

  // Every entry is editable; drafts are keyed by row key (master data) or
  // `telefonat:<label>` (free facts from the phone call). Initialised once —
  // the underlying case state doesn't change while this checkpoint is open.
  const gespeichert = run.caseState.preparation
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    rows.forEach((r) => (initial[r.key] = r.value ?? ""))
    remainingGathered.forEach(
      (g) => (initial[`telefonat:${g.label}`] = g.value)
    )
    // Was beim Abschluss eingetragen wurde, hat Vorrang vor der Vorbefüllung.
    for (const eintrag of gespeichert?.checklist ?? []) {
      const wert = (eintrag as { value?: string }).value
      if (wert) initial[eintrag.key] = wert
    }
    return initial
  })
  const setValue = (key: string, value: string) =>
    setValues((v) => ({ ...v, [key]: value }))

  const done = () => {
    if (submit.isPending) return
    const checklist = [
      ...rows.map((r) => {
        const value = (values[r.key] ?? "").trim()
        return {
          key: r.key,
          checked: value.length > 0,
          value: value || undefined,
        }
      }),
      ...remainingGathered.map((g) => {
        const value = (values[`telefonat:${g.label}`] ?? "").trim()
        return {
          key: `telefonat:${g.label}`,
          label: g.label,
          checked: value.length > 0,
          value: value || undefined,
        }
      }),
    ]
    submit.mutate(
      { checklist, goal },
      {
        onError: () =>
          toast.error(
            "Die Gesprächsvorbereitung konnte nicht gespeichert werden."
          ),
      }
    )
  }

  return (
    // `mt-1` wie beim Nachbereitungsbogen: ohne Beschreibungszeile endet der
    // Kopf direkt an der Leiste. Mit dessen `mb-3` bleiben 16px — dasselbe Maß
    // wie `gap-4` im Raster.
    <div className={cn("mt-1", etappenRaster(vollbild.voll))}>
      <EtappenSpalte voll={vollbild.voll}>
        <Leitfragen einheit="gespraechsvorbereitung" />
        <Card className="gap-0 border-[var(--rule)] p-5">
          <div className="mb-2 text-[11px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
            Zwingend erforderlich
          </div>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {rows.map((r) => (
              <PrepFeld
                key={r.key}
                label={r.label}
                value={values[r.key] ?? ""}
                onChange={(value) => setValue(r.key, value)}
                readOnly={readOnly}
                marker={r.fromPhone ? "Telefonat" : undefined}
              />
            ))}
          </div>

          {remainingGathered.length > 0 && (
            <div className="mt-5">
              <div className="mb-2 text-[11px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
                Aus dem Telefonat
              </div>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {remainingGathered.map((g, i) => (
                  <PrepFeld
                    key={`${g.label}-${i}`}
                    label={g.label}
                    value={values[`telefonat:${g.label}`] ?? ""}
                    onChange={(value) =>
                      setValue(`telefonat:${g.label}`, value)
                    }
                    readOnly={readOnly}
                    variant="telefonat"
                  />
                ))}
              </div>
            </div>
          )}

          <div className="mt-5">
            <label className="text-[13px] font-medium text-muted-foreground">
              Notizen
            </label>
            <Textarea
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              readOnly={readOnly}
              placeholder={
                readOnly
                  ? "—"
                  : "Worauf willst du bei dieser Kundin besonders achten?"
              }
              className="mt-1.5"
            />
          </div>
        </Card>

        {!readOnly && (
          <StickyAction>
            <Button onClick={done} disabled={submit.isPending} size="lg">
              {submit.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Gesprächsvorbereitung abschließen
              <ArrowRight className="h-4 w-4" />
            </Button>
          </StickyAction>
        )}
      </EtappenSpalte>

      {/* Das Sparring steht neben dem Bogen, nicht darunter: die Beraterin
          trägt ein, was sie im Gespräch mit dem Coach herausfindet. */}
      {sparringSession && (
        <CoachSpalte
          voll={vollbild.voll}
          breit={vollbild.breit}
          onUmschalten={vollbild.umschalten}
        >
          <PrepSparring
            runId={run.id}
            sessionId={sparringSession.id}
            coachStyleId={sparringSession.coachStyleId}
            persona={persona}
            initialMessages={sparringSession.coachChat}
            art="vorbereitung"
            empty={{
              eyebrow: "Vorbereitung",
              title: "Was weißt du über diesen Kunden?",
              hint: (coachName) =>
                `${coachName} geht die ${leitfragenAnzahl("gespraechsvorbereitung")} Leitfragen links mit dir durch, geschrieben oder live gesprochen. Was ihr herausarbeitet, trägst du in den Vorbereitungsbogen ein. Kein Feedback, nur die Frage, was du aus den Daten ableitest.`,
            }}
          />
        </CoachSpalte>
      )}
    </div>
  )
}

/**
 * The follow-up unit's first step: the follow-up work along the leading
 * questions.
 *
 * Built like the preparation — the sheet on the left, the conversation about it
 * on the right. Deliberately BEFORE the overall picture: while she writes here
 * no debrief model runs, and what she records feeds the report instead of
 * ending up beside it afterwards.
 */
function StageNachbereitungsbogen({
  run,
  persona,
  readOnly = false,
}: {
  run: ProcessRunDto
  /** For the call in the sparring; without her it stays written. */
  persona?: Persona
  readOnly?: boolean
}) {
  const sparringSession = sessionForUnit(run, "nachbereitung")
  const vollbild = useChatVollbild()
  const submit = useSubmitNachbereitung(run.id)

  // Initialised once: the case state does not change while the sheet is open.
  // A run that stalled before the leading questions existed starts empty.
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const frage of NACHBEREITUNG_FRAGEN) initial[frage.key] = ""
    for (const antwort of run.caseState.nachbereitung?.antworten ?? []) {
      initial[antwort.key] = antwort.value
    }
    return initial
  })

  const done = () => {
    if (submit.isPending) return
    submit.mutate(
      {
        antworten: NACHBEREITUNG_FRAGEN.map((frage) => ({
          key: frage.key,
          value: (values[frage.key] ?? "").trim(),
        })),
      },
      {
        onError: () =>
          toast.error("Die Nachbereitung konnte nicht gespeichert werden."),
      }
    )
  }

  return (
    // `mt-1` statt `mt-6` wie die anderen Etappen: dieser Schritt trägt keine
    // Beschreibungszeile, der Kopf endet also direkt an der Leiste. Zusammen
    // mit dessen `mb-3` bleiben 16px — dasselbe Maß wie `gap-4` im Raster.
    <div className={cn("mt-1", etappenRaster(vollbild.voll))}>
      <EtappenSpalte voll={vollbild.voll}>
        <Leitfragen einheit="nachbereitung" />
        <Card className="gap-0 border-[var(--rule)] p-5">
          <div className="mb-2 text-[11px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
            Nachbereitungsbogen
          </div>
          <div className="grid gap-1.5">
            {NACHBEREITUNG_FRAGEN.map((frage) => (
              <PrepFeld
                key={frage.key}
                label={frage.kurz}
                value={values[frage.key] ?? ""}
                onChange={(value) =>
                  setValues((v) => ({ ...v, [frage.key]: value }))
                }
                readOnly={readOnly}
              />
            ))}
          </div>
        </Card>

        {!readOnly && (
          <StickyAction>
            <Button onClick={done} disabled={submit.isPending} size="lg">
              {submit.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Nachbereitung abschließen
              <ArrowRight className="h-4 w-4" />
            </Button>
          </StickyAction>
        )}
      </EtappenSpalte>

      {/* No session means the run predates the leading questions. The sheet
          then stands alone — it works without the coach. */}
      {sparringSession && (
        <CoachSpalte
          voll={vollbild.voll}
          breit={vollbild.breit}
          onUmschalten={vollbild.umschalten}
        >
          <PrepSparring
            runId={run.id}
            sessionId={sparringSession.id}
            coachStyleId={sparringSession.coachStyleId}
            persona={persona}
            initialMessages={sparringSession.coachChat}
            art="nachbereitung"
            empty={{
              eyebrow: "Nachbereitung",
              title: "Was ist aus dem Termin geworden?",
              hint: (coachName) =>
                `${coachName} geht die ${leitfragenAnzahl("nachbereitung")} Leitfragen links mit dir durch, geschrieben oder live gesprochen. Was ihr klärt, trägst du in den Nachbereitungsbogen ein. Noch keine Bewertung: das Gesamtbild kommt, wenn der Bogen steht.`,
            }}
          />
        </CoachSpalte>
      )}
    </div>
  )
}

/**
 * Die Schlussstation des Durchlaufs: das Gesamtbild und der Coach dazu.
 *
 * Aufbau wie die Gesprächsvorbereitung — links der Gegenstand, rechts das
 * Gespräch darüber. Der Bericht ist derselbe wie auf der Debrief-Seite, nur
 * ohne deren Druckrahmen; der Chat ist der Durchlauf-Coach, der jede Einheit
 * kennt. Seine Vorschlagschips kommen aus dem Debrief selbst, deshalb stehen
 * sie erst, wenn der Strom durch ist.
 */
function StageNachbereitung({
  run,
  readOnly = false,
  onDone,
}: {
  run: ProcessRunDto
  readOnly?: boolean
  onDone?: () => void
}) {
  const advance = useAdvanceStage(run.id)
  const coach = useCoachById(run.coachStyleId)
  const coachName = coachPossessive(coach)
  const [quote, setQuote] = useState<string | null>(null)
  const vollbild = useChatVollbild()
  const { debrief, done, failed, slow, retry } = useDebriefStream(run.id)

  const abschliessen = () => {
    advance.mutate(run.id, {
      onSuccess: () => onDone?.(),
      onError: (error) => toast.error(errorMessage(error)),
    })
  }

  return (
    <div className={cn("mt-6", etappenRaster(vollbild.voll))}>
      <EtappenSpalte voll={vollbild.voll}>
        {failed && !done ? (
          <Card className="items-center border-[var(--rule)] p-8 text-center">
            <AlertCircle className="h-6 w-6 text-primary" />
            <p className="text-[14px] leading-relaxed text-muted-foreground">
              Das Gesamtbild konnte nicht erstellt werden.
            </p>
            <Button type="button" variant="outline" onClick={retry}>
              <RefreshCw className="h-4 w-4" />
              Erneut versuchen
            </Button>
          </Card>
        ) : !debrief ? (
          <DebriefSkeleton />
        ) : (
          // Markieren heftet ein Zitat an den nächsten Beitrag — dieselbe Geste
          // wie auf der Debrief-Seite. Erst wenn der Bericht steht: solange er
          // strömt, macht jedes Neuzeichnen die Auswahl ohnehin kaputt.
          <SelectionQuoteArea
            enabled={done}
            onQuote={setQuote}
            className="space-y-6"
          >
            <DebriefReport
              debrief={debrief}
              runId={run.id}
              coachName={coachName}
              coachLabel={coach?.name}
              coachPortraitUrl={coach?.portraitUrl}
              headingLevel="h2"
              nachbereitung={run.caseState.nachbereitung?.antworten}
            />
          </SelectionQuoteArea>
        )}

        {slow && !done && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-[var(--rule)] bg-card px-4 py-3">
            <span className="text-[13.5px] text-muted-foreground">
              Die Auswertung dauert ungewöhnlich lange.
            </span>
            <Button variant="outline" size="sm" onClick={retry}>
              <RefreshCw className="h-3.5 w-3.5" />
              Erneut versuchen
            </Button>
          </div>
        )}

        {!readOnly && (
          <StickyAction>
            <Button
              onClick={abschliessen}
              disabled={advance.isPending}
              size="lg"
            >
              {advance.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Training abschließen
              <ArrowRight className="h-4 w-4" />
            </Button>
          </StickyAction>
        )}
      </EtappenSpalte>

      <CoachSpalte
        voll={vollbild.voll}
        breit={vollbild.breit}
        onUmschalten={vollbild.umschalten}
      >
        <RunCoachChat
          runId={run.id}
          coachStyleId={run.coachStyleId}
          // Der Coach ist auf die Einheiten-Feedbacks gegründet; bis das
          // Gesamtbild steht, bleibt das Feld gesperrt.
          ready={done}
          initialMessages={run.coachChat}
          suggestedQuestions={
            done
              ? debrief?.suggestedQuestions?.filter(
                  (q): q is string => typeof q === "string"
                )
              : undefined
          }
          quote={quote}
          onQuoteClear={() => setQuote(null)}
          scroll="inner"
        />
      </CoachSpalte>
    </div>
  )
}

/**
 * Das Rollenspiel-Protokoll einer abgeschlossenen Phase, nur zum Lesen.
 *
 * Die Auswertung allein beantwortet nicht, warum eine Note zustande kam. Wer
 * "Beleg" liest, will die Stelle im Gespräch sehen können.
 */
function TranscriptView({
  messages,
  personaName,
}: {
  messages: { role: string; content: string; createdAt?: string }[]
  personaName: string
}) {
  if (messages.length === 0) {
    return (
      <p className="py-8 text-center text-[14px] text-muted-foreground">
        In dieser Einheit wurde nichts gesprochen.
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-3 py-1">
      {messages.map((m, i) => {
        const beraterin = m.role === "user"
        return (
          <div
            key={i}
            className={cn(
              "flex flex-col gap-1",
              beraterin ? "items-end" : "items-start"
            )}
          >
            <span className="text-[11px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
              {beraterin ? "Du" : personaName}
            </span>
            <div
              className={cn(
                "max-w-[80%] rounded-lg px-3.5 py-2.5 text-[14.5px] leading-relaxed",
                beraterin
                  ? "bg-primary/[0.06] text-foreground"
                  : "border border-[var(--rule)] bg-card text-foreground/90"
              )}
            >
              {m.content}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** Shows the just-completed stage's grounded feedback (polls until ready). */
/** Content-shaped placeholder that mirrors the FeedbackPanel while it loads. */
/** Shown when the feedback stream failed or timed out — the transcript is
 *  safe server-side, so retrying just re-runs the (idempotent) generation. */
/**
 * Ob im Stream schon etwas steht, was das Blatt zeigen kann.
 *
 * `abschlussBeitrag` zählt ausdrücklich nicht: er streamt als Erstes, landet
 * aber als Nachricht im Verlauf und nicht im Blatt. Ginge die Spalte darauf
 * auf, stünde sie wieder leer da — genau der Zustand, den diese Prüfung
 * vermeidet. Weil die Feldreihenfolge des Schemas feststeht, heißt „irgendein
 * anderes Feld hat Inhalt" zugleich: der Abschluss ist fertig gestreamt.
 */
function blattHatInhalt(streaming?: FeedbackLike): boolean {
  if (!streaming) return false
  return Object.entries(streaming).some(([feld, wert]) => {
    if (feld === "abschlussBeitrag") return false
    if (Array.isArray(wert)) return wert.filter(Boolean).length > 0
    if (wert && typeof wert === "object") return Object.keys(wert).length > 0
    return !!wert
  })
}

function FeedbackSkeleton() {
  return (
    <div className="relative space-y-6" aria-busy="true">
      <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
        <div className="flex items-center gap-2 rounded-full border border-primary/20 bg-card/90 px-4 py-2.5 text-[13px] font-medium shadow-lg backdrop-blur-sm">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
          Feedback wird erstellt…
        </div>
      </div>

      {/* Stärken */}
      <div className="rounded-lg border border-[var(--rule)]">
        <div className="flex items-baseline justify-between border-b border-[var(--rule)] px-6 py-4">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-3 w-16" />
        </div>
        <div className="divide-y divide-[var(--rule)]">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="space-y-2 px-6 py-4">
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      </div>

      {/* Entwicklungsimpulse */}
      <div className="rounded-lg border border-[var(--rule)]">
        <div className="border-b border-[var(--rule)] px-6 py-4">
          <Skeleton className="h-5 w-56" />
        </div>
        <div className="divide-y divide-[var(--rule)]">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="space-y-2 px-6 py-5">
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ))}
        </div>
      </div>

      {/* Feedback-Dimensionen */}
      <div className="rounded-lg border border-[var(--rule)]">
        <div className="flex items-baseline justify-between border-b border-[var(--rule)] px-6 py-4">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-3 w-16" />
        </div>
        <div className="divide-y divide-[var(--rule)]">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="grid gap-3 px-6 py-5 md:grid-cols-[1fr_auto] md:items-start"
            >
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-2/3" />
              </div>
              <div className="flex items-center gap-3 md:justify-end">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-5 w-10" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bessere Formulierungen */}
      <div className="rounded-lg border border-[var(--rule)]">
        <div className="border-b border-[var(--rule)] px-6 py-4">
          <Skeleton className="h-5 w-48" />
        </div>
        <div className="grid gap-0 md:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className={
                "space-y-3 p-5 " +
                (i === 0
                  ? "border-b border-[var(--rule)] md:border-r md:border-b-0"
                  : "")
              }
            >
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-3 w-4/5" />
            </div>
          ))}
        </div>
      </div>

      {/* Das Schlusswort des Coaches: ohne Rahmen, wie im fertigen Blatt. */}
      <div className="flex gap-3 border-t border-[var(--rule)] px-1 pt-5">
        <Skeleton className="h-6 w-6 shrink-0 rounded-full" />
        <div className="w-full space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      </div>
    </div>
  )
}

/**
 * Das Reflexionsgespräch nach einer abgeschlossenen Phase.
 *
 * Früher stand hier die Auswertung und der Coach daneben, für Rückfragen. Jetzt
 * ist es umgekehrt: die Seite beginnt leer, der erste Gesprächsanteil gehört der
 * Beraterin, und die Bilanz erscheint erst, wenn sie sie anfordert.
 *
 * Der ganze Streaming-Apparat für das Feedback ist damit entfallen — Watchdogs,
 * stiller Neuversuch, Fehlerkarte. Er war nötig, weil beim Öffnen der Seite ein
 * sichtbarer Stream hängen konnte. Hier startet beim Öffnen nichts.
 */
function StageReview({
  sessionId,
  persona,
  units,
  onDone,
  rueckblick = false,
  einzelEinheit = false,
}: {
  sessionId: string
  persona: Persona
  /** The run's units, which decide how far the report reaches. */
  units: TrainingsEinheit[]
  onDone: () => void
  /** True when an already finished unit was opened again afterwards. */
  rueckblick?: boolean
  /** A run over exactly this one unit: nothing follows it. */
  einzelEinheit?: boolean
}) {
  const qc = useQueryClient()
  const { data: session } = useSession(sessionId)
  const { data: reflexion } = useReflection(sessionId)
  const coach = useCoachById(session?.coachStyleId)
  const coachName = coachPossessive(coach)
  const advance = useAdvanceReflection(sessionId)

  const stufe = reflexion?.stage ?? "einstieg"
  // Hat die Beraterin die Reflexion ausdrücklich übersprungen? Dann will sie die
  // Auswertung sehen, auch die aus dem Protokoll. Wer nur weiterklickt, sieht sie
  // nicht — sonst wäre die fertige Auswertung durch die Hintertür zurück.
  const uebersprungen = (reflexion?.stageLog ?? []).some(
    (e) => e.nach === "analyse" && e.ausloeser === "nutzer"
  )
  const feedback =
    session?.feedback &&
    (session.feedback.herkunft === "dialog" || uebersprungen)
      ? session.feedback
      : undefined

  const [quote, setQuote] = useState<string | null>(null)
  const [protokollOffen, setProtokollOffen] = useState(false)
  const protokoll = session?.messages ?? []

  /**
   * Units with `dokument: "nurEinzeln"` get no document of their own inside a
   * longer run.
   *
   * The feedback is still produced — it carries the IDD record and the debrief's
   * competency averages — it just does not open as its own sheet. After a
   * two-minute phone call a document would be more apparatus than insight; the
   * content goes into the summary instead.
   */
  const eigenesDokument =
    !session?.unit || zeigtEigenesDokument(session.unit, units.length)

  // Das Feedback lebt neben dem Gespräch, nicht darüber: es entsteht aus dem
  // Verlauf und gehört chronologisch an dessen Ende, als letzter Beitrag des
  // Coaches plus Karte. Die Spalte fährt beim Erzeugen auf und lässt sich
  // schließen; im Rückblick steht sie offen, dafür kommt man ja.
  const [feedbackOffen, setFeedbackOffen] = useState(rueckblick)
  // Steht das Feedback, führt das Dokument und der Verlauf klappt ein. Nicht
  // „Dokument vergrößern": das Gespräch bleibt als Griff über der Eingabeleiste
  // erreichbar, und wer schreibt, bekommt es von selbst zurück.
  const [chatEingeklappt, setChatEingeklappt] = useState(false)
  /** Where the chat's pinned bar lands: under both columns, not inside one. */
  const [leiste, setLeiste] = useState<HTMLDivElement | null>(null)
  // Wer die Seite mit vorhandenem Feedback öffnet, kommt dafür: die Spalte
  // steht dann offen. Einmal beim Laden, danach entscheidet sie selbst.
  const spalteInitial = useRef(false)
  useEffect(() => {
    if (spalteInitial.current || !session) return
    spalteInitial.current = true
    if (feedback && eigenesDokument) {
      setFeedbackOffen(true)
      setChatEingeklappt(true)
    }
  }, [session, feedback, eigenesDokument])
  const breit = useMediaQuery("(min-width: 1024px)")
  // Der Abschluss des Coaches streamt zuerst und ist in dem Moment noch keine
  // Nachricht — der Server schreibt ihn erst am Ende in den Verlauf. Bis der
  // Verlauf neu aufgebaut ist, steht hier eine Bubble aus dem Stream. Ein
  // eigener Zustand statt einer Ableitung, damit sie im selben Render
  // verschwindet, in dem der Neuaufbau greift: sonst fehlt der Abschluss
  // einen Frame lang.
  const [attrappe, setAttrappe] = useState(false)
  const [verlaufEpoche, setVerlaufEpoche] = useState(0)
  const verlaufHatAbschluss = (session?.coachChat ?? []).some(
    (m) => m.istAbschluss
  )

  // Die Eröffnung des Coaches schreibt der Server, wenn die Reflexionssitzung
  // angelegt wird. Der Verlauf kann parallel dazu geladen worden sein und sie
  // deshalb noch nicht enthalten — und `useChat` seedet seinen Thread beim
  // Mount, eine nachgeladene Nachricht käme nicht mehr an. Also einmal
  // nachladen, BEVOR der Chat steht.
  const [verlaufBereit, setVerlaufBereit] = useState(false)
  useEffect(() => {
    if (verlaufBereit || !session || !reflexion) return
    if ((session.coachChat?.length ?? 0) > 0) {
      setVerlaufBereit(true)
      return
    }
    void qc
      .invalidateQueries({
        queryKey: sessionsControllerGetQueryKey({ path: { id: sessionId } }),
      })
      // Auch im Fehlerfall freigeben: ein Verlauf ohne Eröffnung ist unschöner,
      // aber benutzbar. Hier zu warten hieße, die Seite hängen zu lassen.
      .finally(() => setVerlaufBereit(true))
  }, [session, reflexion, verlaufBereit, sessionId, qc])

  const {
    object,
    submit,
    isLoading: feedbackLaeuft,
    error: feedbackFehler,
  } = useObjectStream({
    api: `/api/sessions/${sessionId}/feedback/dialog/stream`,
    socketRequest: { kind: "dialog-feedback", sessionId },
    schema: feedbackSchema,
    onError: () => setAttrappe(false),
    onFinish: ({ object }) => {
      if (!object) {
        setAttrappe(false)
        return
      }
      void (async () => {
        // Erst nachladen, dann neu aufbauen: der Abschluss steht jetzt als
        // echte Nachricht im Verlauf, und `useChat` liest den nur beim Mount.
        const key = sessionsControllerGetQueryKey({ path: { id: sessionId } })
        await qc.invalidateQueries({ queryKey: key })
        await qc.invalidateQueries({
          queryKey: reflectionControllerGetQueryKey({
            path: { id: sessionId },
          }),
        })
        // Nur neu aufbauen, wenn der Abschluss wirklich angekommen ist. Fehlt
        // er (der Server konnte ihn nicht schreiben), bleibt die gestreamte
        // Bubble stehen — sie hat den Text ja. Sonst verschwände Norberts
        // letzter Satz genau in dem Moment, in dem das Feedback fertig ist.
        const frisch = qc.getQueryData<TrainingSessionDto>(key)
        if (frisch?.coachChat?.some((m) => m.istAbschluss)) {
          setVerlaufEpoche((n) => n + 1)
          setAttrappe(false)
        }
      })()
    },
  })

  // Kein Effect, der beim Öffnen startet: das Feedback entsteht auf Wunsch.
  const feedbackErstellen = () => {
    // Steht schon ein Abschluss im Verlauf, braucht es keine zweite Bubble.
    setAttrappe(!verlaufHatAbschluss)
    // Die Spalte bleibt hier zu. Sie geht erst auf, wenn das Blatt etwas zu
    // zeigen hat — siehe den Effect unten. Bis dahin lädt das Feedback im
    // Hintergrund, der Verlauf behält die ganze Breite, und dass etwas
    // entsteht, sagt die Karte im Verlauf („Wird erstellt …").
    // Ohne `refresh` liefert der Server ein vorhandenes Feedback aus, statt es
    // erneut zu bezahlen. Ein Nachziehen gibt es in der Oberfläche nicht: das
    // Feedback ist das Ende des Gesprächs, nicht ein Stand, der mitläuft.
    submit({})
  }
  // Behind a ref: `useChat` keeps the callback from its first render, which
  // would see a stale feedback state.
  const festhalten = useRef(() => {})
  useEffect(() => {
    festhalten.current = () => {
      if (!feedback && !feedbackLaeuft) feedbackErstellen()
    }
  })
  const vorDerAnalyse = stufe === "einstieg" || stufe === "selbstreflexion"
  const springtGerade = advance.isPending || feedbackLaeuft
  /**
   * Holt die Sicht des Coaches ins Gespräch.
   *
   * Zwei Schritte, und die Reihenfolge ist tragend: erst die Stufe, dann die
   * Frage. Umgekehrt antwortet der Coach noch unter seinem Zuhören-Auftrag und
   * weicht der Frage aus, statt sie zu beantworten.
   *
   * Die Frage geht als IHRE Nachricht in den Verlauf. Das ist der Unterschied
   * zu den abgeschafften Antwortvorschlägen: eine Frage an ihn nimmt ihr nichts
   * weg, eine vorformulierte Selbsteinschätzung schon.
   */
  const analyseEroeffnen = (frag: (text: string) => void) =>
    advance.mutate(
      {
        path: { id: sessionId },
        body: { ziel: "analyse", ausloeser: "nutzer" },
      },
      { onSuccess: () => frag("Was ist dir aufgefallen?") }
    )
  /**
   * Der Ausweg vor dem ersten eigenen Beitrag: direkt zum Feedback.
   *
   * Der Server übergeht seine Freigabebedingungen, wenn der Mensch es verlangt.
   * Der Sprung erzeugt auch das Feedback — sonst landete man auf einer leeren
   * Seite und müsste einen zweiten Knopf finden.
   */
  const reflexionUeberspringen = () =>
    advance.mutate(
      {
        path: { id: sessionId },
        body: { ziel: "analyse", ausloeser: "nutzer" },
      },
      { onSuccess: () => feedbackErstellen() }
    )
  const streaming = (object ?? undefined) as FeedbackLike | undefined
  const zeigeFeedback = !!feedback || feedbackLaeuft || !!streaming
  // Der eine Umschaltpunkt: ab hier hat das Blatt Inhalt, und ab hier ist auch
  // der Abschluss des Coaches fertig gestreamt.
  const blattBereit = !!feedback || blattHatInhalt(streaming)

  /**
   * Die Spalte geht beim ersten Inhalt auf, nicht beim Start.
   *
   * Vorher stand sie im Ladezustand da und nahm dem Verlauf die Breite weg,
   * ohne etwas zu zeigen. Läuft nur einmal: `blattBereit` kippt von falsch auf
   * wahr und bleibt dann. Wer die Spalte danach von Hand zumacht, bekommt sie
   * nicht wieder aufgedrückt.
   *
   * Die zwei Frames sind die Bewegung: die Spalte muss einmal in ihrer
   * Startbreite gestanden haben, sonst gibt es nichts, wovon das Wachsen
   * ausgehen könnte. Ein Frame reicht nicht — React malt den ersten Zustand
   * erst im nächsten.
   */
  useEffect(() => {
    if (!blattBereit || !eigenesDokument) return
    setFeedbackOffen(true)
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setChatEingeklappt(true))
    )
  }, [blattBereit, eigenesDokument])

  // Wait for the session before mounting the chat: useChat seeds its thread
  // on mount, so a late-arriving coachChat would otherwise be dropped.
  if (!session || !verlaufBereit) return <ReviewSkeleton />

  const anzeige = (feedback ?? streaming) as FeedbackLike | undefined

  // Without a document of its own the card is replaced by the unit's three-line
  // balance, right in the thread: the coach's spoken closing carries the unit,
  // and the balance is what NL-20 means by "goes into the summary".
  const karte = eigenesDokument ? (
    <FeedbackKarte
      phaseLabel={session.unit ? EINHEIT_LABELS[session.unit] : undefined}
      feedback={anzeige}
      laeuft={feedbackLaeuft}
      offen={feedbackOffen}
      onOpen={() => setFeedbackOffen(true)}
    />
  ) : (
    <BilanzZeilen
      feedback={anzeige}
      laeuft={feedbackLaeuft}
      einheit={session.unit}
    />
  )

  const feedbackNode = (
    <>
      <ReportMasthead
        title={coachName ? `${coachName} Feedback` : "Feedback"}
        coachName={coachName}
        personaName={persona.name}
        phaseLabel={session.unit ? EINHEIT_LABELS[session.unit] : undefined}
        date={session.startedAt}
      />
      <SelectionQuoteArea enabled={!!feedback} onQuote={setQuote}>
        {anzeige ? (
          <FeedbackPanel
            feedback={anzeige}
            coachName={coachName}
            coachStyleId={session.coachStyleId}
            // Nur hier: das lebende Blatt hält der Bilanz den Platz frei,
            // weil sie als letzter sichtbarer Abschnitt einläuft.
            laeuft={feedbackLaeuft}
          />
        ) : (
          <FeedbackSkeleton />
        )}
      </SelectionQuoteArea>
      <ReportFooter />
    </>
  )

  const aboveThread = (
    <>
      {/* Eine Zeile, nicht zwei verlorene Absätze: der Rahmen des Gesprächs
          gehört zusammen und nicht in die Mitte des leeren Schirms. */}
      <div className="no-print flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-[var(--rule)] pb-3">
        <PhaseScopeNote
          einheit={session.unit}
          einzelEinheit={units.length === 1}
        />
        {protokoll.length > 0 && (
          <button
            type="button"
            onClick={() => setProtokollOffen((v) => !v)}
            className="shrink-0 text-[12.5px] font-medium text-muted-foreground hover:text-foreground"
          >
            {protokollOffen
              ? "Protokoll ausblenden"
              : `Gesprächsprotokoll · ${protokoll.length}`}
          </button>
        )}
      </div>
      {protokollOffen && protokoll.length > 0 && (
        <div className="no-print mt-4">
          <TranscriptView messages={protokoll} personaName={persona.name} />
        </div>
      )}
      {feedbackFehler && !feedback && (
        <div className="no-print mt-4 flex items-center justify-between gap-3 rounded-md border border-[var(--rule)] bg-card px-4 py-3">
          <span className="text-[13.5px] text-muted-foreground">
            Das Feedback konnte nicht erstellt werden.
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => feedbackErstellen()}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Erneut versuchen
          </Button>
        </div>
      )}
    </>
  )

  const spalteOffen = feedbackOffen && zeigeFeedback && eigenesDokument

  return (
    // Eingabe und Abschlussknopf liegen unter beiden Spalten, nicht unter dem
    // Verlauf: sie gehören zur Einheit, nicht zum Gespräch. Deshalb reicht die
    // Seite dem Chat einen Platz für seine Leiste (`barSlot`), statt sie in der
    // Spalte zu lassen, wo sie neben dem Blatt eingequetscht säße.
    <div className="flex h-full min-h-0 flex-col print:block">
      {/* Die Spalten wandern ineinander, statt umzuspringen: das Dokument wächst
        an die Stelle, an der das Gespräch stand. Beide Zustände sind dieselbe
        Form (zwei `minmax`-Spuren), sonst gäbe es nichts zu interpolieren. */}
      <div
        className={cn(
          "grid min-h-0 flex-1 gap-3 transition-[grid-template-columns] duration-300 ease-out motion-reduce:transition-none print:block",
          spalteOffen &&
            !chatEingeklappt &&
            "lg:grid-cols-[minmax(0,1fr)_minmax(360px,44%)]",
          spalteOffen &&
            chatEingeklappt &&
            "lg:grid-cols-[minmax(0,0fr)_minmax(0,100%)]"
        )}
      >
        {/* Eingeklappt läuft die Spalte auf null Breite, statt zu verschwinden —
        `display:none` wäre ein Sprung statt einer Bewegung. `min-w-0` ist dafür
        tragend: ohne es hielte der Verlauf die Spur auf seiner Mindestbreite
        offen. Gemountet bleibt sie ohnehin, damit der Verlauf nicht neu lädt,
        und `inert` hält Fokus und Vorlesen draußen, solange sie unsichtbar ist. */}
        <div
          inert={spalteOffen && chatEingeklappt && breit}
          className={cn(
            "min-h-0 min-w-0 transition-opacity duration-200 motion-reduce:transition-none print:hidden",
            spalteOffen &&
              chatEingeklappt &&
              breit &&
              "overflow-hidden opacity-0"
          )}
        >
          <CoachChat
            barSlot={leiste}
            sessionId={sessionId}
            persona={persona}
            coachStyleId={session.coachStyleId}
            stufe={stufe}
            initialMessages={session.coachChat}
            aboveThread={aboveThread}
            verlaufEpoche={verlaufEpoche}
            // Der Abschluss und die Bilanz hängen unter dem Verlauf, nicht darin.
            // Ohne dieses Signal bliebe die Seite beim Erzeugen des Feedbacks stehen.
            scrollSignal={`${attrappe}|${feedbackLaeuft}|${anzeige?.abschlussBeitrag?.length ?? 0}|${
              anzeige?.zusammenfassung?.transfer ? 1 : 0
            }`}
            // Wer im eingeklappten Zustand schreibt, will die Antwort sehen.
            beforeSend={() => setChatEingeklappt(false)}
            // Während der Abschluss des Coaches in denselben Verlauf streamt,
            // liefe eine Frage gegen ihn an. Danach nicht mehr: sobald das
            // Blatt Inhalt hat, ist der Abschluss geschrieben, und die
            // Beraterin darf weiterfragen, während die Details noch streamen.
            ready={!feedbackLaeuft || blattBereit}
            // Die Karte hängt an der Nachricht, mit der der Coach das Feedback
            // übergeben hat — nicht am Ende, denn danach darf weitergeredet werden.
            // Unter dem Abschluss, nicht darüber: erst spricht der Coach, dann
            // steht die Bilanz dazu. Dieselbe Folge wie beim Erzeugen, wo der
            // Abschluss als erstes Feld streamt — sonst spränge die Karte beim
            // Neuaufbau des Verlaufs.
            afterMessage={(m) =>
              (m.metadata as { istAbschluss?: boolean } | undefined)
                ?.istAbschluss ? (
                <div className="pl-11">{karte}</div>
              ) : null
            }
            afterThread={
              attrappe ? (
                <div className="space-y-3">
                  {/* Keine Tippblase, solange nur gewartet wird: die Karte lädt
                      schon sichtbar, zwei Indikatoren nebeneinander sind einer
                      zu viel. Der Abschluss erscheint, wenn er Text hat — und
                      zwar über der Karte, wie im fertigen Verlauf auch. */}
                  {streaming?.abschlussBeitrag ? (
                    <CoachBubble
                      role="assistant"
                      text={streaming.abschlussBeitrag}
                      streaming={feedbackLaeuft}
                      coachStyleId={session.coachStyleId}
                    />
                  ) : null}
                  <div className="pl-11">{karte}</div>
                </div>
              ) : zeigeFeedback && !verlaufHatAbschluss ? (
                // Feedback ohne Abschluss im Verlauf: Bestandsdaten von vor der
                // Übergabe, oder der Server konnte den Beitrag nicht schreiben. Die
                // Karte hängt dann ans Ende, damit das Feedback erreichbar bleibt.
                <div className="pl-11">{karte}</div>
              ) : undefined
            }
            quote={quote}
            onQuoteClear={() => setQuote(null)}
            onFinish={(message) => {
              void qc.invalidateQueries({
                queryKey: sessionsControllerGetQueryKey({
                  path: { id: sessionId },
                }),
              })
              void qc.invalidateQueries({
                queryKey: reflectionControllerGetQueryKey({
                  path: { id: sessionId },
                }),
              })
              if (hatFeedbackFestgehalten(message)) festhalten.current()
            }}
            onVoiceComplete={async () => {
              // Awaited, nicht abgefeuert: der Verlauf wird danach neu aufgebaut und
              // muss die Turns des Anrufs dann schon enthalten.
              await qc.invalidateQueries({
                queryKey: sessionsControllerGetQueryKey({
                  path: { id: sessionId },
                }),
              })
              await qc.invalidateQueries({
                queryKey: reflectionControllerGetQueryKey({
                  path: { id: sessionId },
                }),
              })
            }}
            einstiegsAktion={
              <Button
                type="button"
                variant="ghost"
                onClick={reflexionUeberspringen}
                disabled={springtGerade}
                className="text-muted-foreground"
              >
                {springtGerade
                  ? "Feedback wird erstellt …"
                  : "Reflexion überspringen"}
              </Button>
            }
            trailing={({ ask, hatEigenenBeitrag, canSend }) => {
              if (rueckblick) {
                return (
                  <Button onClick={onDone} size="lg" variant="outline">
                    Zurück zur aktuellen Einheit
                  </Button>
                )
              }
              // Bevor sie etwas gesagt hat, gibt es nichts vorwärts. Der Ausweg steht
              // dezent unter der Eröffnung, ein Hauptknopf hier wäre die Einladung,
              // die Reflexion zu umgehen.
              if (!hatEigenenBeitrag && !zeigeFeedback) return null

              // Der Weg in die gemeinsame Analyse. Ist der Coach von selbst dorthin
              // gegangen, ist dieser Zustand schon vorbei und der Knopf zeigt das
              // Feedback an — deshalb hängt er an der Stufe, nicht an einem Klick.
              if (vorDerAnalyse) {
                return (
                  <Button
                    size="lg"
                    onClick={() => analyseEroeffnen(ask)}
                    disabled={springtGerade || !canSend}
                  >
                    {springtGerade
                      ? "Einen Moment …"
                      : coachName
                        ? `Jetzt ${coachName} Sicht`
                        : "Jetzt seine Sicht"}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                )
              }

              if (!zeigeFeedback) {
                return (
                  <Button
                    size="lg"
                    onClick={() => feedbackErstellen()}
                    disabled={feedbackLaeuft || !canSend}
                  >
                    {feedbackLaeuft ? "Wird erstellt …" : "Feedback erstellen"}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                )
              }

              // Solange die Bilanz entsteht, führt hier kein Weg weiter:
              // `zeigeFeedback` ist schon wahr, wenn der Stream läuft, und ein
              // Klick nähme ihr das Feedback, für das sie gerade wartet.
              return (
                <Button onClick={onDone} size="lg" disabled={feedbackLaeuft}>
                  {feedbackLaeuft
                    ? "Wird erstellt …"
                    : einzelEinheit
                      ? "Training abschließen"
                      : "Weiter zur nächsten Einheit"}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )
            }}
          />
        </div>

        {/* Breit: die Spalte neben dem Gespräch, wie ein geöffnetes Dokument. Der
        Verlauf ist no-print, die Spalte nicht — gedruckt wird das Feedback. */}
        {/* Kein <aside>: die Druckregeln blenden `aside` aus, weil damit die
        App-Sidebar gemeint ist — und dieses Element IST der Druck. */}
        {spalteOffen && breit && (
          <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-[var(--rule)] bg-card print:border-0">
            <div className="no-print flex items-center justify-between gap-3 border-b border-[var(--rule)] px-4 py-2.5">
              <span className="text-[11.5px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
                Feedback
              </span>
              <div className="flex items-center gap-1">
                {/* `anzeige`, damit der Knopf schon während des Streamens
                    steht und ausgegraut erklären kann, worauf man wartet.
                    Gesprochen wird trotzdem nie ein halbes Feedback: `disabled`
                    hängt am fertig persistierten Objekt. */}
                <VorleseButton
                  feedback={anzeige ?? {}}
                  voice={coach?.voice}
                  sessionId={sessionId}
                  disabled={!feedback}
                  iconOnly
                />
                <PrintButton iconOnly />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => setChatEingeklappt((v) => !v)}
                  aria-label={
                    chatEingeklappt
                      ? "Gespräch einblenden"
                      : "Gespräch ausblenden"
                  }
                  className="h-7 w-7"
                >
                  {chatEingeklappt ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => {
                    setFeedbackOffen(false)
                    setChatEingeklappt(false)
                  }}
                  aria-label="Feedback schließen"
                  className="h-7 w-7"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 print:p-0">
              {/* Ohne das Gespräch daneben wächst der Text nicht mit — eine
              Lesespalte bleibt eine Lesespalte, nur mittig statt rechts. */}
              <div className={cn(chatEingeklappt && "mx-auto max-w-[760px]")}>
                {feedbackNode}
              </div>
            </div>
          </section>
        )}

        {/* Schmal: dasselbe Dokument als eingezogenes Blatt von rechts. */}
        <Sheet
          open={spalteOffen && !breit}
          onOpenChange={(open) => {
            if (!open) setFeedbackOffen(false)
          }}
        >
          <SheetContent
            side="right"
            className="w-full overflow-y-auto sm:max-w-lg"
          >
            {/* pr-11 hält die Zeile frei vom absolut gesetzten Schließen-Knopf
              des Blatts. */}
            <SheetHeader className="flex-row items-center justify-between gap-2 pr-11">
              <SheetTitle>Feedback</SheetTitle>
              <div className="flex items-center gap-1">
                <VorleseButton
                  feedback={anzeige ?? {}}
                  voice={coach?.voice}
                  sessionId={sessionId}
                  disabled={!feedback}
                  iconOnly
                />
                <PrintButton iconOnly />
              </div>
            </SheetHeader>
            <div className="px-4 pb-6">{feedbackNode}</div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Der Platz für die Leiste des Chats. Sie bleibt auch stehen, wenn der
        Verlauf eingeklappt ist: darüber steht dann der Griff, und Abschicken
        klappt das Gespräch wieder auf — das Feld führt also nicht ins Leere.
        Der Griff steht neben dem Portalziel, nicht darin: React hängt die
        Leiste an genau diesen Knoten. */}
      <div className="no-print shrink-0">
        {spalteOffen && chatEingeklappt && breit && (
          <ChatHandle
            coachStyleId={session.coachStyleId}
            onOpen={() => setChatEingeklappt(false)}
          />
        )}
        <div ref={setLeiste} />
      </div>
    </div>
  )
}

/**
 * Die Karte im Verlauf, mit der der Coach das Feedback übergibt.
 *
 * Nicht das Dokument selbst: das steht rechts. Hier nur, was darin ist und der
 * Weg dorthin — damit das Gespräch mit einem Beitrag endet und nicht mit einem
 * Bericht mitten im Chat.
 */
/**
 * The balance of a unit without a document of its own, right in the thread.
 *
 * Three lines instead of a card that would open a sheet which does not exist.
 * After a two-minute phone call that is the right amount of evaluation;
 * everything beyond it is carried by the debrief over the whole run.
 */
function BilanzZeilen({
  feedback,
  laeuft,
  einheit,
}: {
  feedback?: FeedbackLike
  laeuft: boolean
  einheit?: TrainingsEinheit
}) {
  const bilanz = feedback?.zusammenfassung ?? undefined
  const zeilen = [
    { label: "Stärke", value: bilanz?.staerke, breite: "w-4/5" },
    { label: "Hebel", value: bilanz?.hebel, breite: "w-2/3" },
    { label: "Transfer", value: bilanz?.transfer, breite: "w-3/4" },
  ]
  // Ohne laufenden Aufruf und ohne Inhalt gibt es nichts zu zeigen — etwa bei
  // Alt-Feedback, das dieses Feld nie hatte.
  if (!laeuft && !zeilen.some((z) => z.value)) return null

  return (
    <div
      className="reveal max-w-[560px] overflow-hidden rounded-xl border border-[var(--rule)] bg-card"
      aria-busy={laeuft || undefined}
    >
      {/* Überschrift wie auf der Feedback-Karte: ohne sie steht hier ein Kasten
          ohne Absender, und es ist nicht erkennbar, dass das die Auswertung ist.
          Sie steht von Anfang an — die Karte lädt, statt erst zu erscheinen. */}
      <div className="flex items-center gap-2.5 border-b border-[var(--rule)] px-4 py-2.5">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent text-primary">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
        </span>
        <span className="text-[13.5px] font-medium">
          Feedback{einheit ? ` · ${EINHEIT_LABELS[einheit]}` : ""}
        </span>
      </div>
      <dl className="divide-y divide-[var(--rule)]">
        {zeilen.map((z) => (
          <div key={z.label} className="px-4 py-2.5">
            <dt className="text-[11px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
              {z.label}
            </dt>
            <dd className="mt-0.5 text-[13.5px] leading-relaxed">
              {z.value ?? (
                // Die drei Zeilen kommen nacheinander aus dem Stream. Jede
                // wartende Zeile steht als Balken da, damit die Karte ihre Höhe
                // behält und nicht bei jedem Satz nachspringt.
                <span className="flex flex-col gap-1.5 py-1" aria-hidden>
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className={cn("h-3", z.breite)} />
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function FeedbackKarte({
  phaseLabel,
  feedback,
  laeuft,
  offen,
  onOpen,
}: {
  phaseLabel?: string
  feedback?: FeedbackLike
  laeuft: boolean
  offen: boolean
  onOpen: () => void
}) {
  const staerken = (feedback?.strengths ?? []).filter(Boolean).length
  const impulse = (feedback?.impulses ?? []).filter(Boolean).length
  const hatTransfer = !!(
    feedback?.transfer?.naechsteAnwendung || feedback?.transfer?.konkreterSatz
  )
  const inhalt = [
    staerken > 0 && `${staerken} ${staerken === 1 ? "Stärke" : "Stärken"}`,
    impulse > 0 && `${impulse} ${impulse === 1 ? "Impuls" : "Impulse"}`,
    hatTransfer && "Transfer",
  ].filter(Boolean) as string[]

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "reveal no-print flex w-full max-w-[420px] items-center gap-3 rounded-xl border bg-card px-4 py-3 text-left transition-colors",
        "hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
        offen ? "border-primary/40" : "border-[var(--rule)]"
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-primary">
        <Sparkles className="h-4.5 w-4.5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-medium">
          Feedback{phaseLabel ? ` · ${phaseLabel}` : ""}
        </span>
        <span className="block truncate text-[12.5px] text-muted-foreground">
          {laeuft && inhalt.length === 0
            ? "Wird erstellt …"
            : inhalt.length > 0
              ? inhalt.join(" · ")
              : "Stärken, Impulse, Transfer"}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-1 text-[12.5px] font-medium text-primary">
        {laeuft ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          <>
            {offen ? "Offen" : "Öffnen"}
            {!offen && <ArrowRight className="h-3.5 w-3.5" aria-hidden />}
          </>
        )}
      </span>
    </button>
  )
}

/** Platzhalter, solange die Sitzung lädt. */
function ReviewSkeleton() {
  return (
    <div className="flex items-center gap-3 p-8 text-[14px] text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Wird geladen…
    </div>
  )
}

/**
 * Das Arbeitsblatt neben dem Gespräch.
 *
 * Als eigene Komponente, weil es an zwei Orten hängt: breit in der rechten
 * Spalte, schmal im eingezogenen Blatt. Zwei Kopien wären beim nächsten Feld
 * auseinandergelaufen.
 */
/**
 * The sheet beside the conversation: counter and progress in the head, the
 * case briefing as body, and — where the caller passes one — the phase action
 * docked at the foot. Head and foot frame the same story: fill these, then
 * finish here.
 */
function Arbeitsblatt({
  run,
  persona,
  vorgeschichte,
  einheit,
  onWerkzeug,
  action,
}: {
  run: ProcessRunDto
  persona: Persona
  vorgeschichte?: string[]
  einheit: TrainingsEinheit
  /** Öffnet das AV-Tool. Nur da, wo die Bedarfsanalyse trainiert wird. */
  onWerkzeug?: () => void
  action?: ReactNode
}) {
  const { erfasst, gesamt } = arbeitsblattStand(einheit, run.caseState)
  const felder = arbeitsblattFelder(einheit)

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-[var(--rule)] bg-card">
      <div className="shrink-0 px-4 pt-3 pb-3">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[14px]">
            {einheitDefinition(einheit).shortLabel}
          </span>
          <span
            className="flex items-baseline gap-1 tabular-nums"
            aria-live="polite"
          >
            <span
              className={cn(
                "text-[16px] font-semibold",
                erfasst === gesamt && "text-success"
              )}
            >
              {erfasst}
            </span>
            <span className="text-[13px] text-muted-foreground">
              / {gesamt} erfasst
            </span>
          </span>
        </div>
        <div
          className="mt-2 grid gap-0.5"
          style={{ gridTemplateColumns: `repeat(${gesamt}, minmax(0, 1fr))` }}
          aria-hidden
        >
          {Array.from({ length: gesamt }, (_, i) => (
            <span
              key={i}
              className={cn(
                "h-[5px] rounded-[1px]",
                i < erfasst ? "bg-primary" : "bg-muted"
              )}
            />
          ))}
        </div>
      </div>
      {/* Das Blatt hält fest, was im Gespräch GESAGT wurde; das Werkzeug
          daneben, was gerechnet wird. Deshalb steht der Einstieg hier: es ist
          dieselbe Sache, eine Stufe weiter. */}
      {onWerkzeug && (
        <div className="shrink-0 border-t border-[var(--rule)] px-4 py-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onWerkzeug}
            className="w-full gap-1.5"
          >
            <Calculator className="h-3.5 w-3.5" />
            AV-Tool öffnen
          </Button>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto border-t border-[var(--rule)] px-4 py-3">
        <CaseBriefingCard
          fall={persona.fall}
          felder={felder}
          needsAnalysis={run.caseState.needsAnalysis}
          appointment={run.caseState.appointment}
          vorgeschichte={vorgeschichte}
        />
        {/* The notes taken in the preparation checkpoint travel with the case
            state into every later unit of the run. */}
        {run.caseState.preparation?.goal?.trim() && (
          <div className="mt-4 border-t border-[var(--rule)] pt-3">
            <div className="text-[13px] text-muted-foreground">
              Deine Notizen
            </div>
            <p className="mt-1 text-[13.5px] leading-relaxed whitespace-pre-wrap text-foreground/85">
              {run.caseState.preparation.goal}
            </p>
          </div>
        )}
      </div>
      {action && (
        <div
          className={cn(
            "shrink-0 border-t border-[var(--rule)] p-3",
            erfasst === gesamt && "bg-success/5"
          )}
        >
          {erfasst === gesamt && (
            <p className="mb-2 flex items-center gap-1.5 text-[12.5px] text-success">
              <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Alles erfasst. Schließe die Einheit ab, um weiterzugehen.
            </p>
          )}
          {action}
        </div>
      )}
    </div>
  )
}

function DurchlaufRun() {
  const { runId } = Route.useParams()
  const navigate = useNavigate()
  // An IDD scenario is entered from the IDD area and leads back there.
  const iddSzenario = Boolean(useIddRunStand(runId).data?.training)
  const zurueck = iddSzenario ? "/idd" : "/durchlauf"
  // What is on screen. null means: the running unit.
  // Zwei Wege führen hierher, und sie unterscheiden sich im Abschluss-Knopf:
  // frisch beendet (dann geht es weiter) und nachgeschlagen (dann zurück).
  const [viewingUnit, setViewingUnit] = useState<TrainingsEinheit | null>(null)
  const [reviewIstRueckblick, setReviewIstRueckblick] = useState(false)

  const { data: run, isLoading } = useProcessRun(runId, {
    refetchInterval: (q) =>
      q.state.data?.status === "active" && !viewingUnit ? 4000 : false,
  })
  const { data: personas = [] } = usePersonas()
  const persona = personas.find((p) => p.id === run?.personaId)

  /**
   * A run over exactly one unit gets no debrief.
   *
   * The debrief pulls several units into one picture. Over a single one it
   * aggregates nothing, it repeats that unit's reflection — and costs a model
   * call for it. The unit's result is the balance from the reflection
   * conversation, and that is where it stays.
   */
  const einzelEinheit = run?.units.length === 1

  /**
   * Trägt der Plan die Nachbereitung, endet der Durchlauf in seiner eigenen
   * Leiste: die Schlussstation IST die Auswertung. Wer ihn später wieder
   * öffnet, landet dort und nicht auf der Debrief-Seite — die bleibt als
   * Archiv über die Historie erreichbar.
   */
  const gesamtbildEinheit = run?.units.find((u) =>
    istGesamtbild(u as TrainingsEinheit)
  ) as TrainingsEinheit | undefined

  // A completed run without a closing station has nothing to show here — go
  // straight to the debrief. This also covers reopening such a run from the
  // history table.
  const completedNow =
    run?.status === "completed" &&
    !viewingUnit &&
    !einzelEinheit &&
    !gesamtbildEinheit
  useEffect(() => {
    if (!completedNow) return
    void navigate({
      to: "/durchlauf/$runId/debrief",
      params: { runId },
      replace: true,
    })
  }, [completedNow, navigate, runId])

  if (isLoading || !run) {
    return (
      <div className="flex items-center gap-3 p-8 text-[14px] text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> {GESPRAECHSTRAINING_LABEL}{" "}
        wird geladen…
      </div>
    )
  }

  const currentUnit = run.currentUnit as TrainingsEinheit | undefined
  const completed = run.status === "completed"
  const name = persona?.name ?? "die Kundin"

  // Cluster text + body per state.
  let description: string | undefined
  let body: ReactNode
  let headerInBody = false

  // Picking a unit means: leave the running one, or come back to it.
  const waehleEinheit = (unit: TrainingsEinheit) => {
    // A run over one unit has nothing to switch between, and looking back at the
    // running unit would be looking back at itself.
    if (einzelEinheit || unit === currentUnit) {
      setViewingUnit(null)
      setReviewIstRueckblick(false)
      return
    }
    setReviewIstRueckblick(true)
    setViewingUnit(unit)
  }

  const rueckblickSession = viewingUnit
    ? sessionForUnit(run, viewingUnit)
    : undefined

  if (viewingUnit === "gespraechsvorbereitung") {
    // The preparation has no evaluation, but it has the filled-in checklist and
    // the sparring with the coach.
    body = <StagePrep run={run} persona={persona} readOnly />
  } else if (viewingUnit && istGesamtbild(viewingUnit)) {
    // Nachgeschlagen: derselbe Bericht, nur ohne den Knopf, der den Durchlauf
    // beendet — er ist längst beendet.
    body = <StageNachbereitung run={run} readOnly />
  } else if (viewingUnit && rueckblickSession && persona) {
    body = (
      <StageReview
        // Load-bearing: without it React reuses the instance across a unit
        // switch, `started` stays true so no stream is submitted for the new
        // unit, and the panel keeps showing the previous unit's evaluation.
        key={rueckblickSession.id}
        sessionId={rueckblickSession.id}
        persona={persona}
        units={run.units as TrainingsEinheit[]}
        rueckblick={reviewIstRueckblick}
        einzelEinheit={einzelEinheit}
        onDone={() => {
          // Over a single unit the training ends here: no debrief, so no
          // intermediate step that prepares for one either.
          if (einzelEinheit) {
            void navigate({ to: "/" })
            return
          }
          // Leaving the last unit's review finishes the whole run — mark the
          // fresh completion so the debrief fires its confetti exactly once
          // (reopening an old run later stays quiet).
          if (completed && !reviewIstRueckblick) {
            sessionStorage.setItem(`debrief-feier-${runId}`, "1")
          }
          setViewingUnit(null)
          setReviewIstRueckblick(false)
        }}
      />
    )
  } else if (completed && einzelEinheit && persona) {
    // No debrief, so the one unit's reflection stays the destination — also when
    // the run is reopened from the history later.
    const einzige = sessionForUnit(run, run.units[0] as TrainingsEinheit)
    body = einzige ? (
      <StageReview
        key={einzige.id}
        sessionId={einzige.id}
        persona={persona}
        units={run.units as TrainingsEinheit[]}
        einzelEinheit
        onDone={() => void navigate({ to: "/" })}
      />
    ) : null
  } else if (completed && gesamtbildEinheit) {
    // Abgeschlossen und mit Schlussstation: der Bericht bleibt dort, wo er
    // entstanden ist. Ohne Knopf — es gibt nichts mehr abzuschließen. Und ohne
    // Beschreibungszeile: der Bericht erklärt sich selbst.
    body = <StageNachbereitung run={run} readOnly />
  } else if (completed) {
    // The redirect effect above is already navigating to the debrief.
    body = (
      <div className="mt-8 flex items-center justify-center gap-3 text-[14px] text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Debrief wird geöffnet…
      </div>
    )
  } else if (currentUnit) {
    description = einheitBeschreibung(currentUnit, name)

    if (istGesamtbild(currentUnit)) {
      // Two steps in one unit: the follow-up work first, then the report. The
      // answers IN the case state are the switch — that way the step survives a
      // reload without a cursor of its own.
      if (!run.caseState.nachbereitung) {
        // Ohne Zeile: die Leitfragen stehen direkt darunter und sagen besser,
        // was zu tun ist, als ein Satz über ihnen.
        description = undefined
        body = <StageNachbereitungsbogen run={run} persona={persona} />
      } else {
        // Ohne Beschreibungszeile wie im Rückblick: der Bericht erklärt sich
        // selbst.
        description = undefined
        body = (
          <StageNachbereitung
            run={run}
            // Der Durchlauf ist damit zu Ende. Kein Sprung auf die
            // Debrief-Seite: dieselbe Auswertung zweimal hintereinander wäre
            // eine Dublette.
            onDone={() => void navigate({ to: "/" })}
          />
        )
      }
    } else if (einheitDefinition(currentUnit).interaktion === "sparring") {
      // Ohne Zeile wie beim Nachbereitungsbogen: die Leitfragen stehen direkt
      // darunter und sagen besser, was zu tun ist, als ein Satz über ihnen.
      description = undefined
      body = <StagePrep run={run} persona={persona} />
    } else {
      const session = sessionForUnit(run, currentUnit)
      body =
        session && persona ? (
          <StageRoleplay
            key={session.id}
            run={run}
            session={session}
            persona={persona}
            onAdvanced={(_id, unit, mitFeedback) => {
              setReviewIstRueckblick(false)
              // Without feedback the running unit is already the next one.
              setViewingUnit(mitFeedback ? unit : null)
            }}
            onSelectUnit={waehleEinheit}
          />
        ) : (
          <div className="mt-6 text-center text-[14px] text-muted-foreground">
            Diese Einheit wird vorbereitet…
          </div>
        )
      headerInBody = Boolean(session && persona)
    }
  } else {
    description = "Diese Einheit wird vorbereitet…"
    body = (
      <div className="mt-6 text-center">
        <Button
          variant="outline"
          onClick={() => void navigate({ to: zurueck })}
        >
          {iddSzenario ? "Zum IDD-Bereich" : "Zur Übersicht"}
        </Button>
      </div>
    )
  }

  return (
    // No page heading any more: the navigation already carries the name, and the
    // line was taking space from the stage. The way in is the unit rail itself.
    <div className="mx-auto flex h-full min-h-0 w-full max-w-6xl flex-col px-5 py-4 sm:px-6">
      <Link
        to={zurueck}
        className="no-print mb-2 inline-flex w-fit shrink-0 items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {iddSzenario ? "Zum IDD-Bereich" : "Zur Übersicht"}
      </Link>
      {!headerInBody && (
        <div className="no-print mb-3 shrink-0">
          <StageHeader
            run={run}
            description={description}
            viewing={viewingUnit ?? currentUnit ?? gesamtbildEinheit}
            // Die Bilanz gehört noch zur gezeigten Einheit: der Cursor steht
            // schon weiter, angefangen hat die nächste aber niemand.
            imAbschluss={!!viewingUnit && !reviewIstRueckblick}
            onSelect={waehleEinheit}
          />
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
    </div>
  )
}

export const Route = createFileRoute("/_app/durchlauf/$runId")({
  component: DurchlaufRun,
})
